// Translation dictionaries and helpers for English and Polish.

import { getLocale, type Locale } from "./lib/locale";

export function t(key: keyof typeof pl, params: Record<string, string> = {}) {
  const locale = getLocale();
  const translations = locale === "pl" ? pl : en;
  let str = translations[key] ?? key;

  // Replace placeholders with actual values from params
  for (const param in params) {
    str = str.replace(`{{${param}}}`, params[param]);
  }

  return str;
}

// Get a translation function for a specific locale (useful for landing pages with forced locale)
export function getTranslationFn(locale: Locale) {
  return (key: keyof typeof pl, params: Record<string, string> = {}) => {
    const translations = locale === "pl" ? pl : en;
    let str = translations[key] ?? key;

    for (const param in params) {
      str = str.replace(`{{${param}}}`, params[param]);
    }

    return str;
  };
}

// English translations
const en = {
  // I use it to not lose typing when calling t() function but also to be able to call it on arbitrary validation errors
  __generic: "__generic",

  Language: "Language",
  Save: "Save",
  Delete: "Delete",
  Restore: "Restore",
  Back: "Back",
  None: "None",

  // Auth
  // Projects
  "Add project": "Add project",
  "New project": "New project",
  "Delete project": "Delete project",
  "Archive project": "Archive project",
  "Restore project": "Restore project",
  "Archived projects": "Archived projects",
  "Archive nav": "Archive",
  "Search archived projects...": "Search archived projects...",
  "Project archived": "Project archived",
  "Project restored": "Project restored",
  "Project name": "Project name",
  Projects: "Projects",
  "Search projects...": "Search projects...",
  "No projects found": "No projects found",
  "No projects yet": "No projects yet",
  "Create a project to start uploading documents and asking questions.":
    "Create a project to start uploading documents and asking questions.",
  Topic: "Topic",
  Document: "Document",
  "Show only my projects": "Show only my projects",
  "Filter projects": "Filter projects",
  Question: "Question",
  "Project with that name already exists":
    "Project with that name already exists",
  "Project with that name already exists, including archived projects":
    "Project with that name already exists, including archived projects",
  "Name should have at least 3 letters": "Name should have at least 3 letters",
  "Name cannot be empty": "Name cannot be empty",
  source: "source",
  "Are you sure you want to delete this project?":
    "Are you sure you want to delete this project?",
  "Project settings": "Project settings",
  "Private project": "Private project",
  Private: "Private",
  Shared: "Shared",
  Public: "Public",
  "Only you can access this project": "Only you can access this project",
  "Who can access": "Who can access",
  "Only me": "Only me",
  "Specific people": "Specific people",
  "Everyone in organization": "Everyone in organization",
  "Shared with specific people": "Shared with specific people",
  "Add member...": "Add member...",
  "Project members": "Project members",
  "No members yet": "No members yet",
  "Deleted user": "Deleted user",
  Owner: "Owner",
  "Project description": "Project description",
  "Add a description...": "Add a description...",
  "Click to add a description for this project...":
    "Click to add a description for this project...",
  "No project description.": "No project description.",
  "Start a new conversation...": "Start a new conversation...",
  "Restore to start a new conversation": "Restore to start a new conversation",
  "Restore project to continue this conversation":
    "Restore project to continue this conversation",
  "This project is archived": "This project is archived",
  "Archived on {{date}}. You can still review its content, but editing, uploads, and new conversations are disabled.":
    "Archived on {{date}}. You can still review its content, but editing, uploads, and new conversations are disabled.",
  "Archived {{time}}": "Archived {{time}}",
  "Archive this project? It will be removed from active lists, remain viewable, and can be restored later.":
    "Archive this project? It will be removed from active lists, remain viewable, and can be restored later.",
  "Archived projects stay viewable but are removed from active workflows until restored.":
    "Archived projects stay viewable but are removed from active workflows until restored.",
  "No archived projects": "No archived projects",
  "Archive a project to move it out of your active list.":
    "Archive a project to move it out of your active list.",
  "Try a different search term.": "Try a different search term.",
  You: "You",

  // Documents
  "Attach documents": "Attach documents",
  "Delete document": "Delete document",
  "Failed to delete document": "Failed to delete document",
  Documents: "Documents",
  "Upload documents": "Upload documents",
  "Uploading...": "Uploading...",
  "Processing...": "Processing...",
  "Uploading {{file}} failed": "Uploading {{file}} failed",
  "Uploaded {{time}}": "Uploaded {{time}}",
  "File too big": "File too big",
  "{{file}} is too big. Max file size is {{size}}.":
    "{{file}} is too big. Max file size is {{size}}.",
  "{{file}} already exists": "{{file}} already exists",
  "Invalid file type": "Invalid file type",
  "{{file}} is not a supported file type.":
    "{{file}} is not a supported file type.",
  "Drop files here to upload": "Drop files here to upload",
  "Read only": "Read only",
  "File processing failed. Remove the file and try again.":
    "File processing failed. Remove the file and try again.",
  Error: "Error",
  "Document not found": "Document not found",
  "Rename document": "Rename document",
  "Document name": "Document name",
  "Document extension mismatch": "Document extension mismatch",

  // Topics
  Topics: "Topics",
  Messages: "Messages",
  "Messages limit": "Messages limit",
  "Recent topics": "Recent topics",
  "New topic": "New topic",
  "Created {{time}}": "Created {{time}}",
  "Delete topic": "Delete topic",
  Untitled: "Untitled",
  "Topic name": "Topic name",
  "Rename topic": "Rename topic",

  // Chat related
  "Thank you for your feedback!": "Thank you for your feedback!",
  "We'll use this to improve our responses.":
    "We'll use this to improve our responses.",
  "Bad answer": "Bad answer",
  "Good answer": "Good answer",
  "We're glad this response was helpful.":
    "We're glad this response was helpful.",
  "Message copied to clipboard": "Message copied to clipboard",
  "Searching documents": "Searching documents",
  "Searching sources...": "Searching sources...",
  "Source search complete": "Source search complete",
  "Searching legal provisions...": "Searching legal provisions...",
  "Provisions search complete": "Provisions search complete",
  "Searching tax interpretations...": "Searching tax interpretations...",
  "Tax interpretations search complete": "Tax interpretations search complete",
  "Searching procurement notices...": "Searching procurement notices...",
  "Procurement notices search complete": "Procurement notices search complete",
  "Reading provision...": "Reading provision...",
  "Provision loaded": "Provision loaded",
  "Looking up act...": "Looking up act...",
  "Act details loaded": "Act details loaded",
  "Tracing amendments...": "Tracing amendments...",
  "Amendment chain loaded": "Amendment chain loaded",
  "No relevant provisions found": "No relevant provisions found",
  Context: "Context",
  "Searching Supreme Court rulings...": "Searching Supreme Court rulings...",
  "Supreme Court search complete": "Supreme Court search complete",
  "Supreme Court ruling": "Supreme Court ruling",
  "Source document": "Source document",
  "Open source document": "Open source document",
  "Inline preview is not available for this source.":
    "Inline preview is not available for this source.",
  "Unable to load judgment content.": "Unable to load judgment content.",
  "Case number": "Case number",
  Court: "Court",
  Division: "Division",
  "Judgment date": "Judgment date",
  "Judgment type": "Judgment type",
  Judges: "Judges",
  Content: "Content",
  "Researching Polish law...": "Researching Polish law...",
  "Polish law research complete": "Polish law research complete",
  "Connecting to legal database": "Connecting to legal database",
  "Searching Polish acts": "Searching Polish acts",
  "Reading act details": "Reading act details",
  "Synthesizing legal findings": "Synthesizing legal findings",
  "Polish law agent error": "Polish law agent error",
  "No subagent activity yet": "No subagent activity yet",
  "MCP endpoint": "MCP endpoint",
  "Tool calls count": "Tool calls: {{count}}",
  "Tool call identifier": "Tool call ID",
  "Recent actions": "Recent actions",
  Warnings: "Warnings",
  "No legal findings available": "No legal findings available",
  Citation: "Citation",
  "LegalSource.SEJM": "Sejm ELI (DU/MP)",
  "LegalSource.SEJM.description":
    "Search acts from Sejm ELI namespace (Dziennik Ustaw, Monitor Polski)",
  "LegalSource.Eureka": "Tax Interpretations (Eureka)",
  "LegalSource.Eureka.description":
    "Search Polish tax interpretations from the Eureka corpus",
  "LegalSource.EURegulations": "EU Regulations",
  "LegalSource.EURegulations.description":
    "Search EU regulations and compliance requirements (GDPR, NIS2, DORA, AI Act, and more)",
  "LegalSource.BZP": "Public Procurement Notices (BZP/TED)",
  "LegalSource.BZP.description":
    "Search public procurement notices from BZP/TED with notice-level details",
  "LegalSource.KRSCurrent": "KRS (Current Extract)",
  "LegalSource.KRSCurrent.description":
    "Get current entity registry data from KRS by KRS number",
  "LegalSource.KRSFull": "KRS (Full Extract)",
  "LegalSource.KRSFull.description":
    "Get full historical entity registry data from KRS by KRS number",
  "LegalSource.DU": "Dziennik Ustaw (DU)",
  "LegalSource.DU.description":
    "Search across Polish acts in force as of a date",
  "LegalSource.SN": "Sąd Najwyższy (sn.pl)",
  "LegalSource.SN.description": "Search rulings from sn.pl",
  "LegalSource.SP": "Sądy powszechne (orzeczenia.ms.gov.pl)",
  "LegalSource.SP.description": "Search rulings from orzeczenia.ms.gov.pl",
  "LegalSource.IP": "Interpretacje podatkowe (IP)",
  "LegalSource.IP.description": "Search tax interpretations",
  "LegalSource.TK": "Trybunał Konstytucyjny (ipo.trybunal.gov.pl)",
  "LegalSource.TK.description": "Search rulings from ipo.trybunal.gov.pl",
  "LegalSource.KIO": "Krajowa Izba Odwoławcza (wokanda.uzp.gov.pl)",
  "LegalSource.KIO.description": "Search rulings from wokanda.uzp.gov.pl",
  "Searching common court rulings...": "Searching common court rulings...",
  "Common courts search complete": "Common courts search complete",
  "Searching Constitutional Tribunal rulings...":
    "Searching Constitutional Tribunal rulings...",
  "Constitutional Tribunal search complete":
    "Constitutional Tribunal search complete",
  "Searching National Appeal Chamber rulings...":
    "Searching National Appeal Chamber rulings...",
  "National Appeal Chamber search complete":
    "National Appeal Chamber search complete",
  "Open in SAOS": "Open in SAOS",
  "Relevant content found": "Relevant content found",
  "No relevant documents found": "No relevant documents found",
  "Analyzing...": "Analyzing...",
  "Stop generation": "Stop generation",
  Page: "Page",
  "Copy message": "Copy message",
  "No topics yet": "No topics yet",
  "Calculating...": "Calculating...",
  "Define your prompt...": "Define your prompt...",
  "Prompt name": "Prompt name",
  "Save as .docx": "Save as .docx",
  "AnyLawyer - Answer.docx": "AnyLawyer - Answer.docx",
  "Message is too long ({{count}}/{{max}}). Remove {{over}} characters.":
    "Message is too long ({{count}}/{{max}}). Remove {{over}} characters.",

  // Empty states
  "Create your first topic to start the conversation":
    "Create your first topic to start the conversation",

  // Analysis and reflection states
  "Analysis complete": "Analysis complete",
  "Reflecting on the information...": "Reflecting on the information...",

  "Generating citations": "Generating citations",

  // Context-aware loading states
  "Processing your question": "Processing your question",
  "Reading the results": "Reading the results",
  "Analyzing the results": "Analyzing the results",
  "Thinking about the question": "Thinking about the question",
  "Gathering information": "Gathering information",
  "Reviewing findings": "Reviewing findings",
  "Formulating answer": "Formulating answer",
  "Cross-referencing sources": "Cross-referencing sources",
  "Verifying information": "Verifying information",
  "Building comprehensive response": "Building comprehensive response",

  "Comparison complete": "Comparison complete",
  "Comparing documents...": "Comparing documents...",
  Preview: "Preview",

  // Error pages
  "Lost in the legal maze?": "Lost in the legal maze?",
  "The page you're looking for doesn't exist or has been moved.":
    "The page you're looking for doesn't exist or has been moved.",
  "Go back to main page": "Go back to main page",
  "Something went wrong": "Something went wrong",
  "An unexpected error occurred. Please try again.":
    "An unexpected error occurred. Please try again.",

  // New dashboard translations
  "What can I help you with today?": "What can I help you with today?",
  "Ask questions about your legal documents or start a new conversation":
    "Ask questions about your legal documents or start a new conversation",
  docs: "docs",
  "Ask anything about your legal documents...":
    "Ask anything about your legal documents...",
  documents: "documents",
  "Select project": "Select project",
  // Document tabs and search
  "Search documents...": "Search documents...",
  Project: "Project Documents",
  Organization: "Organization Documents",
  "Legal documents": "Legal Documents",
  "Organization Documents": "Organization Documents",
  "No documents found": "No documents found",
  "Added {{time}}": "Added {{time}}",
  "Other organization members will not be able to see this project":
    "Other organization members will not be able to see this project",
  page: "page",
  pages: "pages",

  // Universal document picker
  "Choose documents from your library. Hover over items to preview content.":
    "Choose documents from your library. Hover over items to preview content.",
  "Choose documents from your organization. Hover over items to preview content.":
    "Choose documents from your organization. Hover over items to preview content.",
  document: "document",
  "No documents available": "No documents available",
  "Select or hover over a document to preview":
    "Select or hover over a document to preview",
  Cancel: "Cancel",
  Done: "Done",
  selected: "selected",
  Attach: "Attach",
  Detach: "Detach",
  "Add to project": "Add to project",
  "Remove from project": "Remove from project",

  // Project creation
  "Projects allow you to attach & organize your own documents and collaborate with your team.":
    "Projects allow you to attach & organize your own documents and collaborate with your team.",

  // Document selection error messages
  "Failed to update universal document selection. Please try again.":
    "Failed to update universal document selection. Please try again.",
  "Failed to update organization document selection. Please try again.":
    "Failed to update organization document selection. Please try again.",

  // Admin Dashboard
  "Admin Tools": "Admin Tools",
  Integrations: "Integrations",
  "Organization Stats": "Organization Stats",
  "Organization metrics and insights": "Organization metrics and insights",
  "Last 7 days": "Last 7 days",
  "Last 30 days": "Last 30 days",
  "Current month": "Current month",
  "Previous month": "Previous month",
  "Pick a date range": "Pick a date range",
  "Quick selection": "Quick selection",
  "Users Active": "Users Active",
  "Projects Created": "Projects Created",
  "Documents Uploaded": "Documents Uploaded",
  "Topics Created": "Topics Created",
  "Messages Sent": "Messages Sent",
  "Users who created projects or sent messages":
    "Users who created projects or sent messages",
  "Organization ID": "Organization ID",
  "Daily Activity": "Daily Activity",
  "Topics created and user messages sent per day":
    "Topics created and user messages sent per day",
  "Most Active Users": "Most Active Users",
  "Users with the most messages sent": "Users with the most messages sent",
  User: "User",
  "Show More": "Show More",
  "Show Less": "Show Less",
  more: "more",
  "Turbopuffer Namespaces": "Turbopuffer Namespaces",
  "Top 50 biggest Turbopuffer namespaces and linked projects.":
    "Top 50 biggest Turbopuffer namespaces and linked projects.",
  Namespace: "Namespace",
  Size: "Size",
  Rows: "Rows",
  "Last accessed": "Last accessed",

  // Model settings
  "Model settings": "Model settings",
  Model: "Model",
  Reasoning: "Reasoning",
  Verbosity: "Verbosity",

  // Reasoning levels
  "reasoning.None": "Fast",
  "reasoning.Medium": "Standard",
  "reasoning.High": "Deep",

  // Verbosity levels
  "verbosity.Low": "Concise",
  "verbosity.Medium": "Normal",
  "verbosity.High": "Detailed",

  // Reasoning effort descriptions
  "reasoning.none": "Instant responses",
  "reasoning.medium": "Longer processing, balanced reasoning",
  "reasoning.high": "Research-grade intelligence",

  // Verbosity descriptions
  "verbosity.low": "Concise responses",
  "verbosity.medium": "Moderate explanations",
  "verbosity.high": "Detailed and thorough discussions",

  // Model selection
  // Experimental model notice

  // Voice input
  "Voice input": "Voice input",
  "Stop voice input": "Stop voice input",
  "Listening...": "Listening...",
  "Transcribing...": "Transcribing...",
  "Click to stop listening": "Click to stop listening",
  "Click to start voice input": "Click to start voice input",
  "Transcribing your voice...": "Transcribing your voice...",

  // Web browsing
  "Web browsing": "Web browsing",
  "Enable web browsing to search the internet":
    "Enable web browsing to search the internet",
  "Enable {{source}}": "Enable {{source}}",
  "Searching the web...": "Searching the web...",
  "Web search complete": "Web search complete",
  "Reading web results": "Reading web results",
  "Web page link": "Web page link",
  "Failed to open web page": "Failed to open web page",
  "Expand details": "Expand details",
  "Collapse details": "Collapse details",

  // Google Drive
  "Google Drive": "Google Drive",
  Connect: "Connect",
  Connected: "Connected",
  Disconnect: "Disconnect",
  "Are you sure you want to disconnect Google Drive?":
    "Are you sure you want to disconnect Google Drive?",
  "AI can search your Google Drive files":
    "AI can search your Google Drive files",
  "Searching Google Drive...": "Searching Google Drive...",
  "Google Drive search complete": "Google Drive search complete",
  "Opening file from Google Drive...": "Opening file from Google Drive...",
  "Google Drive link": "Google Drive link",
  "Connect Google Drive to continue": "Connect Google Drive to continue",
  "We need access to open this Google Drive link.":
    "We need access to open this Google Drive link.",
  "We couldn't open that Google Drive link":
    "We couldn't open that Google Drive link",
  "File opened successfully": "File opened successfully",
  "Failed to open file": "Failed to open file",
  "Open in Google Docs": "Open in Google Docs",
  "Open in Google Sheets": "Open in Google Sheets",
  Found: "Found",
  files: "files",
  "No files found in Google Drive": "No files found in Google Drive",
  "Google Drive (entire)": "Google Drive (entire)",
  "Include entire Google Drive": "Include entire Google Drive",
  "Search across all accessible files (My Drive + Shared Drives).":
    "Search across all accessible files (My Drive + Shared Drives).",
  "Whole drive enabled": "Whole drive enabled",
  "Disable whole-drive search to attach folders or files.":
    "Disable whole-drive search to attach folders or files.",

  // OneDrive
  OneDrive: "OneDrive",
  "Are you sure you want to disconnect OneDrive?":
    "Are you sure you want to disconnect OneDrive?",
  "AI can search your OneDrive files": "AI can search your OneDrive files",
  "Connect OneDrive to attach folders": "Connect OneDrive to attach folders",
  "Your OneDrive connection has expired.":
    "Your OneDrive connection has expired.",
  "Searching OneDrive...": "Searching OneDrive...",
  "OneDrive search complete": "OneDrive search complete",
  "No files found in OneDrive": "No files found in OneDrive",
  "Opening file from OneDrive...": "Opening file from OneDrive...",

  // Prompt Improvement
  "Improve prompt": "Improve prompt",
  "Improving prompt...": "Rewriting...",
  "Rewrite your question to be clearer and more effective":
    "Rewrite your question to be clearer and more effective",

  // Read document pages tool
  "Reading document pages...": "Reading document pages...",
  "Document pages loaded": "Document pages loaded",
  Pages: "Pages",

  // Dropbox
  // Connected Sources
  "Connected Sources": "Connected Sources",

  // Cloud Drives
  "Connect Google Drive to attach folders":
    "Connect Google Drive to attach folders",
  "Failed to update folder attachment. Please try again.":
    "Failed to update folder attachment. Please try again.",
  "Limit reached": "Limit reached",
  "You can attach up to 10 folders or files. Please detach some items first.":
    "You can attach up to 10 folders or files. Please detach some items first.",
  "You can attach up to 25 folders or files. Please detach some items first.":
    "You can attach up to 25 folders or files. Please detach some items first.",
  "Monthly message limit reached. Contact your administrator.":
    "Monthly message limit reached. Contact your administrator.",

  // Google OAuth Settings
  "Google OAuth Configuration": "Google OAuth Configuration",
  "Configure organization-specific Google OAuth credentials for Google Drive integration.":
    "Configure organization-specific Google OAuth credentials for Google Drive integration.",
  "Google OAuth Client ID": "Google OAuth Client ID",
  "Enter your Google OAuth Client ID": "Enter your Google OAuth Client ID",
  "Google OAuth Client Secret": "Google OAuth Client Secret",
  "Enter your Google OAuth Client Secret":
    "Enter your Google OAuth Client Secret",
  "Save OAuth Settings": "Save OAuth Settings",
  "Setup Instructions": "Setup Instructions",
  "and create a new project": "and create a new project",
  "and create OAuth 2.0 Client ID (Web application type)":
    "and create OAuth 2.0 Client ID (Web application type)",
  "Add authorized redirect URI:": "Add authorized redirect URI:",
  "Copy the Client ID and Client Secret to the form above":
    "Copy the Client ID and Client Secret to the form above",
  "Connect Google Drive using the organization's credentials":
    "Connect Google Drive using the organization's credentials",
  "Important:": "Important:",
  "Google Picker API Key": "Google Picker API Key",
  "Enter your Browser API Key (optional)":
    "Enter your Browser API Key (optional)",
  "Picker API key is configured. Leave empty to keep unchanged.":
    "Picker API key is configured. Leave empty to keep unchanged.",
  "Optional - required for the Google Picker file selection flow. Create a Browser API key in your Google Cloud Console credentials.":
    "Optional - required for the Google Picker file selection flow. Create a Browser API key in your Google Cloud Console credentials.",
  "(Optional) Enable the Google Picker API and create a Browser API key for Picker-based file selection":
    "(Optional) Enable the Google Picker API and create a Browser API key for Picker-based file selection",

  // Google Drive Picker
  "Select files from Google Drive": "Select files from Google Drive",
  "Connect Google Drive to select files":
    "Connect Google Drive to select files",
  "No files selected yet.": "No files selected yet.",
  "Already attached": "Already attached",
  "The selected files are already attached.":
    "The selected files are already attached.",
  "You can attach up to 10 files. Please detach some items first.":
    "You can attach up to 10 files. Please detach some items first.",
  "You can attach up to 25 files. Please detach some items first.":
    "You can attach up to 25 files. Please detach some items first.",
  "Google Picker is still loading. Please try again.":
    "Google Picker is still loading. Please try again.",
  "Your Google Drive connection has expired.":
    "Your Google Drive connection has expired.",
  "Failed to load picker configuration.":
    "Failed to load picker configuration.",
  "Failed to open file picker.": "Failed to open file picker.",
  "Failed to update file attachment. Please try again.":
    "Failed to update file attachment. Please try again.",
  Reconnect: "Reconnect",

  // Microsoft OAuth Settings (OneDrive)
  "Microsoft OAuth Configuration": "Microsoft OAuth Configuration",
  "Configure organization-specific Microsoft OAuth credentials for OneDrive integration.":
    "Configure organization-specific Microsoft OAuth credentials for OneDrive integration.",
  "OneDrive OAuth configured": "OneDrive OAuth configured",
  "Configuration required": "Configuration required",
  "Microsoft OAuth Client ID": "Microsoft OAuth Client ID",
  "Enter your Microsoft OAuth Client ID":
    "Enter your Microsoft OAuth Client ID",
  "Microsoft OAuth Client Secret": "Microsoft OAuth Client Secret",
  "Enter your Microsoft OAuth Client Secret":
    "Enter your Microsoft OAuth Client Secret",
  "Use this exact URI in your Microsoft app registration":
    "Use this exact URI in your Microsoft app registration",
  "Saved URI differs from current environment:":
    "Saved URI differs from current environment:",
  "Both Client ID and Client Secret are required":
    "Both Client ID and Client Secret are required",
  "Failed to save OAuth credentials": "Failed to save OAuth credentials",
  "Microsoft OAuth credentials saved successfully":
    "Microsoft OAuth credentials saved successfully",
  "Failed to save credentials": "Failed to save credentials",
  "Redirect URI:": "Redirect URI:",
  Success: "Success",

  // Organization settings
  Organizations: "Organizations",
  "Configure AI model settings for each organization":
    "Configure AI model settings for each organization",
  "No organizations found": "No organizations found",
  "Model updated": "Model updated",
  "Organization model has been updated successfully.":
    "Organization model has been updated successfully.",
  "Gemini 3.1 Pro": "Gemini 3.1 Pro",
  Custom: "Custom",
  Default: "Default",
  "Saving...": "Saving...",
  Status: "Status",
  members: "members",
  pending: "pending",
  "No members": "No members",
  Unknown: "Unknown",
  "Pending invitations": "Pending invitations",
  Invite: "Invite",
  "Invite user": "Invite user",
  "Send an invitation to join": "Send an invitation to join",
  "Email address": "Email address",
  Role: "Role",
  Member: "Member",
  Members: "Members",
  Users: "Users",
  "Drive credentials": "Drive credentials",
  "Google Drive credentials": "Google Drive credentials",
  "OneDrive credentials": "OneDrive credentials",
  "Google Drive credentials are configured for this organization.":
    "Google Drive credentials are configured for this organization.",
  "Google Drive credentials are not configured. Configure them in /app/google-oauth.":
    "Google Drive credentials are not configured. Configure them in /app/google-oauth.",
  "OneDrive credentials are configured for this organization.":
    "OneDrive credentials are configured for this organization.",
  "OneDrive credentials are not configured. Configure them in /app/microsoft-oauth.":
    "OneDrive credentials are not configured. Configure them in /app/microsoft-oauth.",
  Admin: "Admin",
  "Send invitation": "Send invitation",
  "Sending...": "Sending...",
  "Invitation sent": "Invitation sent",
  "New organization": "New organization",
  "Organization created": "Organization created",
  "The organization has been created successfully.":
    "The organization has been created successfully.",
  "Access Revoked": "Access Revoked",
  "Your organization's trial access has been revoked. Please contact us to discuss further details and continue using the platform.":
    "Your organization's trial access has been revoked. Please contact us to discuss further details and continue using the platform.",
  "Contact Us": "Contact Us",
  "Access status updated": "Access status updated",
  "Organization trial access status has been updated.":
    "Organization trial access status has been updated.",
  "Create a new organization": "Create a new organization",
  "Organization name": "Organization name",
  "Enter organization name": "Enter organization name",
  "Verified domain": "Verified domain",
  "Enter verified domain (e.g. example.com)":
    "Enter verified domain (e.g. example.com)",
  "Users signing up with this email domain will be invited automatically.":
    "Users signing up with this email domain will be invited automatically.",
  "Creating...": "Creating...",
  "Create organization": "Create organization",
  "messages ({{period}})": "messages ({{period}})",
  "The invitation has been sent successfully.":
    "The invitation has been sent successfully.",
  "An error occurred.": "An error occurred.",
  "Msg/User Limit": "Msg/User Limit",
  "Msg/Org Limit": "Msg/Org Limit",
  "No limit": "No limit",
  "Limit updated": "Limit updated",
  "Messages per user limit has been updated successfully.":
    "Messages per user limit has been updated successfully.",
  "Messages per organization limit has been updated successfully.":
    "Messages per organization limit has been updated successfully.",
  // Transcriptions
  Transcriptions: "Transcriptions",
  "Transcribe audio files": "Transcribe audio files",
  "New Transcription": "New Transcription",
  "Upload Audio": "Upload Audio",
  "No transcriptions yet": "No transcriptions yet",
  "Upload your first audio file to get started":
    "Upload your first audio file to get started",
  "Failed to delete transcription": "Failed to delete transcription",
  "Transcription uploaded": "Transcription uploaded",
  "Your audio file is being processed": "Your audio file is being processed",
  "Processing transcription...": "Processing transcription...",
  "Transcription processed": "Transcription processed",
  "Transcription failed": "Transcription failed",
  Transcript: "Transcript",
  "Audio file": "Audio file",
  "Supported formats: MP3, WAV":
    "Supported formats: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM",
  "Unsupported audio format":
    "{{filename}} is not a supported audio format. Supported: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM.",
  "Convert to document": "Convert to document",
  "Make this transcript searchable in your projects":
    "Make this transcript searchable in your projects",
  "Converted {{time}}": "Converted {{time}}",
  "Converting...": "Converting...",
  "Already converted": "Already converted",
  "Transcription converted to document": "Transcription converted to document",
  "Include timestamps": "Include timestamps",
  Joined: "Joined",
  "Last active": "Last active",
  Never: "Never",

  // Leaflet
  "Leaflet.metaTitle": "AnyLawyer Leaflet",
  "Leaflet.metaDescription":
    "Printable A4 leaflet describing AnyLawyer and pricing.",
  "Leaflet.logoAlt": "AnyLawyer Logo",
  "Leaflet.toolbarHint": "A4 preview – open print dialog to export to PDF.",
  "Leaflet.print": "Print",
  "Leaflet.title": "Legal Intelligence, Perfected.",
  "Leaflet.subtitle":
    "The secure AI workspace for legal document analysis. Analyze thousands of documents in minutes. Integrates with Google Drive, OneDrive, and your private cloud. Zero data training, 100% grounded citations.",
  "Leaflet.contactLabel": "Contact",
  "Leaflet.trialLabel": "Trial",
  "Leaflet.trialValue": "30-day free trial",
  "Leaflet.whatIsTitle": "What is AnyLawyer?",
  "Leaflet.whatIsBody":
    "Upload contracts, court files, or regulations. Ask questions in natural language. Get answers with clickable citations to exact pages and paragraphs.",
  "Leaflet.aiAndLawyerTitle": "AI + Lawyer",
  "Leaflet.aiCardTitle": "AI",
  "Leaflet.aiCardBody":
    "Automates review, answers questions, and drafts reports–always linked to evidence.",
  "Leaflet.lawyerCardTitle": "Lawyer",
  "Leaflet.lawyerCardBody":
    "Keeps control: verifies sources, reviews reasoning, and advises clients with confidence.",
  "Leaflet.capabilitiesTitle": "Key capabilities",
  "Leaflet.cap1Title": "Automated Due Diligence",
  "Leaflet.cap1Body":
    "Identify and extract specific provisions from hundreds of documents instantly with citation-backed accuracy.",
  "Leaflet.cap2Title": "Instant Case Research",
  "Leaflet.cap2Body":
    "Ask complex questions across your entire case file and get answers rooted in evidence.",
  "Leaflet.cap3Title": "Report Generation",
  "Leaflet.cap3Body":
    "Generate accurate and comprehensive legal memos and client reports directly from your case documents.",
  "Leaflet.cap7Title": "Legal Sources Integration",
  "Leaflet.cap7Body":
    "Search court rulings, tax interpretations, and legislation from official Polish legal databases.",
  "Leaflet.pricingTitle": "Pricing",
  "Leaflet.footerTitle": "Book a demo",
  "Leaflet.footerBody":
    "Email us to see AnyLawyer on your documents and workflows.",
  "Leaflet.tablePlan": "Plan",
  "Leaflet.tableFree": "Free (14 days)",
  "Leaflet.tablePlus": "Plus",
  "Leaflet.tablePro": "Pro",
  "Leaflet.tablePrice": "Price per seat/month",
  "Leaflet.tablePlusPrice": "199zł/seat",
  "Leaflet.tableProPrice": "499zł/seat",
  "Leaflet.tableDocuments": "Documents",
  "Leaflet.tableUnlimited": "Unlimited",
  "Leaflet.tableMessages": "Messages per month",
  "Leaflet.tableExtras": "Extras",
  "Leaflet.tableProExtras": "Transcriptions, Google Drive/OneDrive",

  // Pricing page
  "Pricing.metaTitle": "Pricing — AnyLawyer",
  "Pricing.metaDescription": "Compare AnyLawyer plans and pricing.",
  "Pricing.logoAlt": "AnyLawyer Logo",
  "Pricing.pageTitle": "Pricing",
  "Pricing.saveAsPDF": "Save as PDF",
  "Pricing.headline": "Choose the right plan for your practice",
  "Pricing.features": "Features",
  "Pricing.planFree": "Free",
  "Pricing.planPlus": "Plus",
  "Pricing.planPro": "Pro",
  "Pricing.planEnterprise": "Enterprise",
  "Pricing.trialPeriod": "14-day trial",
  "Pricing.perSeatMonth": "per seat/month",
  "Pricing.free": "Free",
  "Pricing.custom": "Custom",
  "Pricing.monthly": "Monthly",
  "Pricing.annual": "Annual",
  "Pricing.unlimited": "Unlimited",
  "Pricing.advanced": "Advanced",
  "Pricing.allModels": "All models",
  "Pricing.allModelsBYOK": "All + BYOK",
  "Pricing.fast": "Fast",
  "Pricing.allModes": "All modes",
  "Pricing.euOrCustom": "EU or custom",
  "Pricing.contactUs": "Contact us",
  "Pricing.categoryBasics": "Basics",
  "Pricing.categoryLimits": "Usage Limits",
  "Pricing.categoryAI": "AI & Models",
  "Pricing.categoryFeatures": "Features",
  "Pricing.categoryIntegrations": "Integrations",
  "Pricing.categorySecurity": "Security & Compliance",
  "Pricing.featurePrice": "Price per seat",
  "Pricing.featureBilling": "Billing",
  "Pricing.featureSeats": "Seats",
  "Pricing.featureMessagesMonth": "Messages per month",
  "Pricing.featureDocuments": "Documents",
  "Pricing.featureProjects": "Projects",
  "Pricing.featureAIModels": "AI models",
  "Pricing.featureReasoningModes": "Reasoning modes",
  "Pricing.featureCitations": "Grounded citations",
  "Pricing.featureDocumentGeneration": "Document generation",
  "Pricing.featureTranscriptions": "Audio transcriptions",
  "Pricing.featureDocxExport": "Export to DOCX",
  "Pricing.featureGoogleDrive": "Google Drive integration",
  "Pricing.featureOneDrive": "OneDrive integration",
  "Pricing.featureLegalSources": "Polish legal sources",
  "Pricing.featureWebSearch": "Web search",
  "Pricing.featureAPI": "API access",
  "Pricing.featureEncryption": "End-to-end encryption",
  "Pricing.featureDataResidency": "Data residency",
  "Pricing.featureZeroTraining": "Zero data training",
  "Pricing.featureSSO": "SSO (Okta, Azure AD)",
  "Pricing.featureAuditLogs": "Audit logs",
  "Pricing.featurePrivateCloud": "Private cloud deployment",
  "Pricing.ctaText": "Ready to get started?",
  "Pricing.ctaStartTrial": "Start free trial",
  "Pricing.ctaContactSales": "Contact sales",
  "Pricing.legalSourcesFootnote":
    "include: Polish legislation (acts, regulations), tax interpretations, common court and Supreme Court rulings, Constitutional Tribunal rulings, KIO rulings (public procurement), EU regulations (GDPR, NIS2, DORA, AI Act, and more), and public procurement notices (BZP/TED).",
  "Pricing.footerNote": "Prices shown in PLN. VAT not included.",

  // Landing page - Navigation
  "Landing.nav.features": "Features",
  "Landing.nav.security": "Security",
  "Landing.nav.pricing": "Pricing",
  "Landing.nav.howItWorks": "How It Works",
  "Landing.nav.testimonials": "Testimonials",
  "Landing.nav.faq": "FAQ",
  "Landing.nav.login": "Log In",
  "Landing.nav.startTrial": "Start Free Trial",
  "Landing.nav.docs": "Docs",
  "Landing.nav.goToApp": "Go to AnyLawyer",
  "Landing.nav.aboutUs": "About Us",
  "Changelog.emptyState": "No product updates yet.",
  "Changelog.menu.emptyState": "No updates yet.",
  "Changelog.menu.openAriaLabel": "Open changelog",
  "Changelog.menu.whatsNew": "What's new",
  "Changelog.menu.viewAllUpdates": "View all updates",

  // Landing page - Hero section
  "Landing.hero.badge": "AI assistant for lawyers",
  "Landing.hero.titlePrefix": "Supercharge",
  "Landing.hero.titleRotating1": "your law firm",
  "Landing.hero.titleRotating2": "your legal department",
  "Landing.hero.titleRotating3": "your sales team",
  "Landing.hero.titleSuffix": "with AI.",
  "Landing.hero.description":
    "AnyLawyer is a secure AI workspace for lawyers. Analyze documents, search Polish legislation and case law, generate reports – with every answer cited from source. Your data is never used to train models.",
  "Landing.hero.bookDemo": "Book a demo",
  "Landing.hero.cta": "Try free – 14 days",
  "Landing.hero.feature1": "Built-in Polish legal acts",
  "Landing.hero.feature2": "Zero hallucinations – cited from source",
  "Landing.hero.feature3": "Data doesn't train models",
  "Landing.hero.feature4": "EU hosting, GDPR",

  // Landing page - Social proof section
  "Landing.trustedBy": "Trusted by Leading Law Firms",
  "Landing.socialProof.badge": "Hundreds of lawyers use AnyLawyer daily",
  "Landing.socialProof.stat1.value": "100,000+",
  "Landing.socialProof.stat1.label": "Questions answered",
  "Landing.socialProof.stat2.value": "20,000+",
  "Landing.socialProof.stat2.label": "Documents uploaded",
  "Landing.socialProof.stat3.value": "50+",
  "Landing.socialProof.stat3.label": "Law firms onboarded",

  // Landing page - Problem → Solution section
  "Landing.problem.title": "Without AnyLawyer",
  "Landing.problem.item1": "Hours spent manually searching documents",
  "Landing.problem.item2": "Risk of missing a key clause in a contract",
  "Landing.problem.item3":
    "ChatGPT hallucinates – can't trust it for legal matters",
  "Landing.problem.item4":
    "No certainty if AI's answer is based on current law",
  "Landing.solution.title": "With AnyLawyer",
  "Landing.solution.item1":
    "Answer in seconds – with exact citation from the document",
  "Landing.solution.item2":
    "Automatic review of thousands of pages – nothing slips through",
  "Landing.solution.item3":
    "Every answer grounded in source – zero hallucinations",
  "Landing.solution.item4":
    "Built-in access to Journal of Laws, Monitor Polski, and case law",

  // Landing page - Killer Feature section
  "Landing.killerFeature.title":
    "AI assistant with built-in access to Polish law",
  "Landing.killerFeature.description":
    "Search Polish legal sources directly from your workspace – all from one window, fully cited.",
  "Landing.killerFeature.microcopy":
    "The source database is updated automatically. You don't need to check if a given act is current – AnyLawyer does it for you.",
  "Landing.killerFeature.source1": "Journal of Laws",
  "Landing.killerFeature.source2": "Monitor Polski",
  "Landing.killerFeature.source3": "Supreme Court",
  "Landing.killerFeature.source4": "Tax interpretations",
  "Landing.killerFeature.source5": "KIO rulings",
  "Landing.killerFeature.source6": "Common courts",
  "Landing.killerFeature.source7": "EU Regulations",
  "Landing.killerFeature.source7.subtitle": "GDPR, NIS2, DORA, AI Act",
  "Landing.killerFeature.source8": "Public Procurement Notices",
  "Landing.killerFeature.source8.subtitle": "BZP/TED",

  // Landing page - Use Cases section
  "Landing.useCases.title": "How it works in practice",
  "Landing.useCases.tab1.title": "Due Diligence in minutes, not weeks",
  "Landing.useCases.tab1.description":
    "Upload hundreds of contracts, leases, and corporate documents. AnyLawyer automatically identifies key provisions, risks, and non-standard clauses – each cited to the exact paragraph.",
  "Landing.useCases.tab2.title": "Legal research in seconds",
  "Landing.useCases.tab2.description":
    "Ask any legal question. AnyLawyer searches your documents AND Polish legislation and case law, giving you a comprehensive answer with linked sources.",
  "Landing.useCases.tab3.title": "Reports and legal memos in one click",
  "Landing.useCases.tab3.description":
    "Generate professional legal memoranda, due diligence reports, or client summaries directly from document analysis. Export to DOCX.",
  "Landing.useCases.tab4.title": "Every word matters",
  "Landing.useCases.tab4.description":
    "Upload transcripts of hearings, testimonies, or stenograms. AnyLawyer detects inconsistencies, key admissions, and argumentation patterns.",
  "Landing.useCases.tab5.title": "Your assistant for every question",
  "Landing.useCases.tab5.description":
    "Is this clause GDPR compliant? What is the statute of limitations? What are the formal requirements for a lawsuit? Ask freely – answers grounded in Polish law.",

  // Landing page - Trust section
  "Landing.trust.title": "Every answer has its proof.",
  "Landing.trust.description":
    "In legal work, there's no room for guessing. AnyLawyer works differently from generic chatbots – every claim is linked to a specific fragment of a document or legal act.",
  "Landing.trust.citations.title": "Grounded Citations",
  "Landing.trust.citations.description":
    "Every answer contains a clickable reference to the exact place in the document. Verify with a single click.",
  "Landing.trust.auditTrail.title": "Reasoning Trail",
  "Landing.trust.auditTrail.description":
    "See step by step how AI reached its conclusion. Full transparency of the thinking process.",
  "Landing.trust.depthControl.title": "Analysis Depth Control",
  "Landing.trust.depthControl.description":
    "You decide how thorough an analysis you want – from a quick overview to an in-depth study. Adjustable AI reasoning power.",

  // Landing page - Benefits section
  "Landing.benefits.title": "Work Smarter, Not Harder",
  "Landing.benefits.description":
    "AnyLawyer transforms how legal professionals handle document-intensive work.",

  // Landing page - How it works section
  "Landing.howItWorks.title": "Up and Running in Minutes",
  "Landing.howItWorks.description":
    "Three simple steps to transform your legal research workflow.",
  "Landing.howItWorks.step1.title": "Upload Your Documents",
  "Landing.howItWorks.step1.description":
    "Drag and drop contracts, briefs, or entire case files. AnyLawyer processes text documents, spreadsheets, images, and scans – over 10 formats supported.",
  "Landing.howItWorks.step2.title": "Ask Anything",
  "Landing.howItWorks.step2.description":
    "Ask complex legal questions in plain language. Our AI analyzes your entire document corpus to find precise answers.",
  "Landing.howItWorks.step3.title": "Get Cited Answers",
  "Landing.howItWorks.step3.description":
    "Receive detailed analysis with pinpoint citations. Export to DOCX and share with your team.",

  // Landing page - Testimonials section
  "Landing.testimonials.title": "Trusted by Legal Professionals",
  "Landing.testimonials.description":
    "See what lawyers are saying about AnyLawyer.",
  "Landing.testimonials.1.quote":
    "AnyLawyer changed how we do due diligence. What used to take a week, we now do in two days. Every answer is cited – I can finally trust an AI tool.",
  "Landing.testimonials.1.name": "Katarzyna Nowak",
  "Landing.testimonials.1.title": "Partner, Corporate Law",
  "Landing.testimonials.2.quote":
    "As an in-house lawyer, I answer dozens of questions daily. Access to Polish legal acts is a game changer – I don't have to jump between LEX and ChatGPT.",
  "Landing.testimonials.2.name": "Michał Kowalski",
  "Landing.testimonials.2.title": "In-house Lawyer",
  "Landing.testimonials.3.quote":
    "We evaluated several legal AI tools. AnyLawyer was the only one that met our security requirements for client data while delivering genuinely useful results.",
  "Landing.testimonials.3.name": "Anna Wiśniewska",
  "Landing.testimonials.3.title": "Managing Partner",

  // Landing page - Bento grid features
  "Landing.bento.title": "Transform Your Legal Practice",
  "Landing.bento.description":
    "AnyLawyer helps legal professionals save time, reduce errors, and gain deeper insights from their documents.",
  "Landing.bento.dueDiligence.title": "Automated Due Diligence",
  "Landing.bento.dueDiligence.description":
    "Identify and extract specific provisions from hundreds of documents instantly. Automate the review of contracts, leases, and discovery materials with citation-backed accuracy.",
  "Landing.bento.caseResearch.title": "Instant Case Research",
  "Landing.bento.caseResearch.description":
    "Stop manual searching. Ask complex questions across your entire case file and get answers rooted in evidence.",
  "Landing.bento.reportGen.title": "Report Generation",
  "Landing.bento.reportGen.description":
    "Generate accurate and comprehensive legal memos and client reports directly from your case documents.",
  "Landing.bento.deposition.title": "Deposition & Testimony Review",
  "Landing.bento.deposition.description":
    "Process and analyze court transcripts to identify inconsistencies, key admissions, and argumentative patterns.",
  "Landing.bento.citations.title": "Grounded Citations",
  "Landing.bento.citations.description":
    "Every claim is linked directly to the source text for instant verification. Zero black-box answers.",
  "Landing.bento.auditTrail.title": "Audit Trail",
  "Landing.bento.auditTrail.description":
    "Follow the AI's reasoning step-by-step. Understand exactly how a conclusion was reached before advising your client.",
  "Landing.bento.questions.q1": "What are the key provisions in section 3.2?",
  "Landing.bento.questions.q2": "When does this contract expire?",
  "Landing.bento.questions.q3": "What are my obligations under this agreement?",
  "Landing.bento.questions.q4":
    "Is there a non-compete clause in this document?",
  "Landing.bento.questions.q5": "What are the termination conditions?",
  "Landing.bento.questions.q6": "Who are the involved parties?",
  "Landing.bento.questions.q7": "What are the payment terms?",
  "Landing.bento.questions.q8": "Are there any indemnity clauses?",
  "Landing.bento.questions.q9": "What is the governing law?",
  "Landing.bento.questions.q10": "How can this agreement be amended?",

  // Landing page - Complete Legal Toolkit section
  "Landing.toolkit.title": "Complete Legal Toolkit",
  "Landing.toolkit.description":
    "Everything you need to manage complex legal workflows in one secure platform.",
  "Landing.toolkit.dataHeavy.title": "Built for Data-Heavy Litigation",
  "Landing.toolkit.dataHeavy.description":
    "AnyLawyer's Deep Context Analysis supports hundreds of thousands of documents per project, enabling full-corpus discovery.",
  "Landing.toolkit.citations.title": "Citations & Verification",
  "Landing.toolkit.citations.description":
    "All answers are supported by pinpoint citations from the original document. Trust but verify.",
  "Landing.toolkit.privateTenant.title": "Private Tenant Deployment",
  "Landing.toolkit.privateTenant.description":
    "Optional deployment to your own private cloud infrastructure. We support AWS, GCP and Azure.",
  "Landing.toolkit.multiStep.title": "Multi-Step Legal Reasoning",
  "Landing.toolkit.multiStep.description":
    "Our AI breaks down complex legal queries into logical steps to find answers buried across hundreds of files.",
  "Landing.toolkit.integration.title": "Ecosystem Integration",
  "Landing.toolkit.integration.description":
    "AnyLawyer connects to your DMS. Google Drive integration available; SharePoint and OneDrive coming soon.",
  "Landing.toolkit.dataIsolation.title": "Strict Data Isolation",
  "Landing.toolkit.dataIsolation.description":
    "Project-level isolation ensures client data never crosses. Encrypted at rest and in transit. EU-hosted.",
  "Landing.toolkit.export.title": "Export to DOCX",
  "Landing.toolkit.export.description":
    "Export your answers to DOCX format for easy sharing and collaboration.",
  "Landing.toolkit.team.title": "Team Collaboration",
  "Landing.toolkit.team.description":
    "Share documents, insights, prompts, and search results with your team. Create collaborative workspaces for case research and knowledge sharing.",
  "Landing.toolkit.comingSoon": "Coming Soon",

  // Landing page - Security section
  "Landing.security.title": "Enterprise-Ready Compliance & Data Sovereignty",
  "Landing.security.description":
    "We understand that client confidentiality is paramount. That's why AnyLawyer is built with a security-first architecture designed for modern legal practice.",
  "Landing.security.encryption":
    "AES-256 encryption at rest (AWS KMS), TLS 1.2+ in transit",
  "Landing.security.isolation": "Strict project and data isolation",
  "Landing.security.zeroTraining":
    "Zero-training policy: training disabled at API level, DPA signed with all subprocessors",
  "Landing.security.privateCloud":
    "Dedicated private cloud deployment options available",
  "Landing.security.encryptionLabel": "Encryption",
  "Landing.security.dataResidency": "Data Residency",
  "Landing.security.dataResidencyValue": "EU (GDPR Compliant)",
  "Landing.security.auditTrailLabel": "Audit Trail",
  "Landing.security.auditTrailValue": "Immutable Logs",

  // Landing page - Pricing section
  "Landing.pricing.title": "Simple, Transparent Pricing",
  "Landing.pricing.description":
    "Choose the plan that best fits your practice's needs. No hidden fees, no long-term commitments.",

  // Free plan
  // Plus plan

  // Pro plan
  // Enterprise plan
  "Landing.pricing.contactSales": "Contact sales",

  "Landing.pricing.getStarted": "Get started",

  // Features
  // Landing page - FAQ section
  "Landing.faq.title": "Frequently",
  "Landing.faq.title2": "Asked",
  "Landing.faq.title3": "Questions",
  "Landing.faq.subtitle":
    "Everything you need to know about the product and billing.",
  "Landing.faq.q1": "Can AnyLawyer hallucinate like ChatGPT?",
  "Landing.faq.a1":
    "AnyLawyer was designed specifically to avoid this. Every answer is grounded in a specific fragment of a document or legal act and includes a clickable citation. If the AI can't find an answer in the sources, it will tell you directly instead of guessing.",
  "Landing.faq.q2": "Is my client data safe?",
  "Landing.faq.a2":
    "Yes. Your documents are AES-256 encrypted, stored in the EU, and are NEVER used to train AI models. Each project is fully isolated – one client's data never comes in contact with another's.",
  "Landing.faq.q3": "What Polish legal sources are available?",
  "Landing.faq.a3":
    "Journal of Laws, Monitor Polski, case law of common and Supreme courts, Constitutional Tribunal rulings, tax interpretations, KIO rulings (public procurement), EU regulations (GDPR, NIS2, DORA, AI Act, and more), and public procurement notices (BZP/TED). The database is updated automatically.",
  "Landing.faq.q4": "How many documents can I process?",
  "Landing.faq.a4":
    "Hundreds of thousands of documents per project. AnyLawyer was built to handle mass analyses like due diligence, discovery, or regulatory audits.",
  "Landing.faq.q5":
    "Can I integrate AnyLawyer with my document management system?",
  "Landing.faq.a5":
    "Google Drive and OneDrive integrations are currently available. Enterprise customers get dedicated integrations.",
  "Landing.faq.q6": "Can I try it before purchasing?",
  "Landing.faq.a6":
    "Absolutely. Every plan starts with a 14-day free trial. No credit card, no commitments.",

  // Landing page - Thesis section
  "Landing.thesis.headline":
    "Thousands of pages. One conversation. Every answer cited.",
  "Landing.thesis.subline":
    "Drop your case files in, ask in plain language, and get answers traced back to the exact paragraph. No hallucinations. No black boxes.",

  // Landing page - Capabilities section
  "Landing.cap.analysis.title": "Deep Document Intelligence",
  "Landing.cap.analysis.body":
    "Upload hundreds of contracts, briefs, or discovery files. Our models read every page and let you query across the entire corpus – surfacing clauses, risks, and patterns a human reviewer would need weeks to find.",
  "Landing.cap.citations.title": "Every Claim, Traced to Source",
  "Landing.cap.citations.body":
    "No trust-me answers. Every response links to the exact paragraph, page, and document it drew from. Click through to verify in seconds. This is how AI earns a lawyer's confidence.",
  "Landing.cap.security.title": "Confidentiality by Architecture",
  "Landing.cap.security.body":
    "AES-256 encryption at rest and in transit. EU-only data residency. Strict tenant isolation. Zero-training policy – your data is never used to improve any model. Private cloud deployment available.",
  "Landing.cap.workflow.title": "From Upload to Memo in Minutes",
  "Landing.cap.workflow.body":
    "Upload, ask, export. Generate DOCX memos, share research with colleagues, and build on previous conversations. Connects to Google Drive and OneDrive so your documents stay in sync.",

  // Landing page - Security summary (on landing page)
  "Landing.securitySummary.title": "Your data is yours. Period.",
  "Landing.securitySummary.zeroTraining.title": "Zero-training policy",
  "Landing.securitySummary.zeroTraining.description":
    "Your documents and queries are NEVER used to train AI models. This is not a promise – it's the architecture.",
  "Landing.securitySummary.encryption.title": "AES-256 encryption",
  "Landing.securitySummary.encryption.description":
    "Data encrypted at rest and in transit. Banking-grade standard.",
  "Landing.securitySummary.hosting.title": "EU hosting / GDPR",
  "Landing.securitySummary.hosting.description":
    "Data processed exclusively on European Union servers. Full GDPR compliance.",
  "Landing.securitySummary.isolation.title": "Per-project data isolation",
  "Landing.securitySummary.isolation.description":
    "One client's data never crosses with another's. Each project is a separate, sealed container.",
  "Landing.securitySummary.privateCloud.title": "Private cloud (Enterprise)",
  "Landing.securitySummary.privateCloud.description":
    "For firms with the highest requirements – deployment on your own AWS, GCP, or Azure infrastructure.",
  "Landing.securitySummary.cta": "Learn more about security",

  // Security page
  "Security.metaTitle": "Security — AnyLawyer",
  "Security.metaDescription":
    "Learn how AnyLawyer protects your data with enterprise-grade security, EU hosting, and zero-training policy.",
  "Security.hero.title": "Your data is yours. Period.",
  "Security.hero.description":
    "Enterprise-grade security is not an add-on — it's the foundation of AnyLawyer. Every architectural decision starts with data protection.",
  "Security.faq.title": "Security FAQ",
  "Security.faq.q1": "Is my client data safe?",
  "Security.faq.a1":
    "Yes. Your documents are AES-256 encrypted, stored in the EU, and are NEVER used to train AI models. Each project is fully isolated – one client's data never comes in contact with another's.",
  "Security.faq.q2":
    "Can I integrate AnyLawyer with my document management system?",
  "Security.faq.a2":
    "Google Drive and OneDrive integrations are currently available. Enterprise customers get dedicated integrations.",
  "Security.docsLink": "Read full security & privacy documentation",
  "Security.cta.title": "Ready to see it in action?",
  "Security.cta.button": "Book a demo",

  // About page
  "About.metaTitle": "About Us — AnyLawyer",
  "About.metaDescription":
    "Meet the team behind AnyLawyer — building AI tools that empower legal professionals to work smarter.",
  "About.hero.title": "About AnyLawyer",
  "About.hero.description":
    "We're on a mission to empower legal professionals with AI that actually understands the law.",
  "About.mission.title": "Our Mission",
  "About.mission.description":
    "We believe AI should empower lawyers — not replace them. AnyLawyer was built to give every lawyer a tool they can trust: fast, precise, and grounded in the law. We build AI that cites sources instead of guessing, protects data instead of exploiting it, and saves hours of work instead of creating more doubts. Because good technology doesn't complicate — it simplifies.",
  "About.leadership.title": "Founders",
  "About.leadership.michal.role": "CEO",
  "About.leadership.michal.bio":
    "Professor of AI and law. Advisor to the AI Office on large language models. Leader of GRAI at the Ministry of Digital Affairs. Founder of DSK Kancelaria (100+ employees).",
  "About.leadership.maciej.role": "CTO",
  "About.leadership.maciej.bio":
    "Engineering Manager at US-based startups. Ex-Software Engineer at Riot Games. Ex-Solutions Architect at Allegro.",
  "About.leadership.rafal.role": "Head of AI",
  "About.leadership.rafal.bio":
    "Ex-AI Tech Lead at Zapier. Ex-AI Tech Lead at Vendr.",
  "About.company.title": "Company Details",
  "About.company.name": "Name",
  "About.company.nameValue": "ANYLAWYER PROSTA SPÓŁKA AKCYJNA",
  "About.company.registry": "Registry",
  "About.company.registryValue": "Rejestr Przedsiębiorców",
  "About.company.krs": "KRS Number",
  "About.company.krsValue": "0001223474",
  "About.company.nip": "NIP",
  "About.company.nipValue": "7792601628",
  "About.company.regon": "REGON",
  "About.company.regonValue": "54397036000000",
  "About.company.legalForm": "Legal Form",
  "About.company.legalFormValue": "Prosta Spółka Akcyjna",

  // Landing page - Product showcase section
  "Landing.showcase.label": "The Platform",
  "Landing.showcase.headline": "Built for the way lawyers actually work",
  "Landing.showcase.subline":
    "Whether it's a 200-page contract or a 10,000-document discovery room, AnyLawyer scales to the complexity of real legal work.",

  // Landing page - Stats
  "Landing.stat.speed": "Faster document review",
  "Landing.stat.encryption": "Encryption standard",
  "Landing.stat.residency": "Data residency",
  "Landing.stat.training": "Data used for training",

  // Landing page - Process section
  "Landing.process.headline": "Three simple steps.",

  // Landing page - Final CTA section
  "Landing.cta.title": "Ready to work smarter?",
  "Landing.cta.description":
    "Join lawyers who are already saving hours every week. 14 days free, no credit card.",
  "Landing.cta.primaryCta": "Start for free",
  "Landing.cta.secondaryCta": "Book a demo",

  // Landing page - Footer
  "Landing.footer.tagline":
    "Empowering legal professionals with next-generation AI tools.",
  "Landing.footer.product": "Product",
  "Landing.footer.resources": "Resources",
  "Landing.footer.docs": "Documentation",
  "Landing.footer.changelog": "Changelog",
  "Landing.footer.taxInterpretations": "Tax Interpretations",
  "Landing.footer.legal": "Legal",
  "Landing.footer.privacyPolicy": "Privacy Policy",
  "Landing.footer.termsOfService": "Terms of Service",
  "Landing.footer.contactUs": "Email us",
  "Landing.footer.faq": "FAQ",
  "Landing.footer.company": "Company",
  "Landing.footer.aboutUs": "About Us",
  "Landing.footer.copyright": "© 2025 AnyLawyer. All rights reserved.",

  // Organization Documents page
  "Manage documents that are available across all projects in your organization":
    "Manage documents that are available across all projects in your organization",
  "Add Documents": "Add Documents",
  "Add Organization Documents": "Add Organization Documents",
  "Upload Files": "Upload Files",
  "Choose Files": "Choose Files",
  "You can select multiple files. Max file size: {{size}}MB each.":
    "You can select multiple files. Max file size: {{size}}MB each.",
  "Selected Files ({{count}})": "Selected Files ({{count}})",
  "Document name...": "Document name...",
  "Upload {{count}} Document": "Upload {{count}} Document",
  "Upload {{count}} Documents": "Upload {{count}} Documents",
  "Upload Progress": "Upload Progress",
  "{{completed}} / {{total}} completed": "{{completed}} / {{total}} completed",
  "No organization documents yet. Add your first one above.":
    "No organization documents yet. Add your first one above.",
  Name: "Name",
  Created: "Created",
  Actions: "Actions",
  "uploading {{progress}}%": "uploading {{progress}}%",
  queued: "queued",
  "upload complete": "upload complete",
  "upload failed": "upload failed",
  "Unsupported file type": "Unsupported file type",
  "Duplicate file": "Duplicate file",
  "{{file}} is already selected.": "{{file}} is already selected.",
  "No files selected": "No files selected",
  "Please select at least one file to upload.":
    "Please select at least one file to upload.",
  "Missing names": "Missing names",
  "Please provide names for all selected documents.":
    "Please provide names for all selected documents.",
  "Upload started": "Upload started",
  "Starting upload of {{count}} document(s).":
    "Starting upload of {{count}} document(s).",
  "Document uploaded": "Document uploaded",
  "{{name}} has been uploaded and is being processed.":
    "{{name}} has been uploaded and is being processed.",
  "Upload timeout": "Upload timeout",
  "Document upload is taking longer than expected. Please refresh the page to check if it appeared.":
    "Document upload is taking longer than expected. Please refresh the page to check if it appeared.",
  "Document processed": "Document processed",
  "{{name}} has been successfully processed.":
    "{{name}} has been successfully processed.",
  "Document processing failed": "Document processing failed",
  "{{name}} failed to process.": "{{name}} failed to process.",
  Ready: "Ready",
  Processing: "Processing",
  Failed: "Failed",
  "Unknown error": "Unknown error",
  "No organization documents": "No organization documents",
  "Preparing shared document": "Preparing shared document",
  "Status updates appear automatically": "Status updates appear automatically",
  "Are you sure you want to delete this document?":
    "Are you sure you want to delete this document?",

  // ELI Legal Acts Browser
  "Legal Acts Database": "Legal Acts Database",
  "Browse and search Polish legal acts from the official ELI database":
    "Browse and search Polish legal acts from the official ELI database",
  "Search acts by title...": "Search acts by title...",
  Search: "Search",
  Clear: "Clear",
  Publishers: "Publishers",
  "No publishers found. Run the Sejm sync to populate the database.":
    "No publishers found. Run the Sejm sync to populate the database.",
  acts: "acts",
  "total acts": "total acts",
  "Legal Acts": "Legal Acts",
  Year: "Year",
  Position: "Position",
  "All years": "All years",
  Showing: "Showing",
  Acts: "Acts",
  "Acts from": "Acts from",
  "All acts from this publisher": "All acts from this publisher",
  "No acts found for the selected criteria.":
    "No acts found for the selected criteria.",
  Previous: "Previous",
  of: "of",
  Next: "Next",
  "Search results for": "Search results for",
  "No acts found matching your search.": "No acts found matching your search.",
  Address: "Address",
  Date: "Date",
  "Recently ingested acts": "Recently ingested acts",
  "Last 10 acts added to the database, ordered by creation time":
    "Last 10 acts added to the database, ordered by creation time",
  "Created at": "Created at",
  "Synced at": "Synced at",
  Indexed: "Indexed",
  "Turbopuffer indexing": "Turbopuffer indexing",
  "Coverage of acts indexed into Turbopuffer":
    "Coverage of acts indexed into Turbopuffer",
  "Total acts in DB": "Total acts in DB",
  "Indexed acts": "Indexed acts",
  Coverage: "Coverage",
  Publisher: "Publisher",
  "Total in DB": "Total in DB",
  Yes: "Yes",
  No: "No",
  "In force": "In force",
  "Not in force": "Not in force",
  Metadata: "Metadata",
  ELI: "ELI",
  Type: "Type",
  "Announcement Date": "Announcement Date",
  "Entry Into Force": "Entry Into Force",
  "Valid From": "Valid From",
  Promulgation: "Promulgation",
  "Expiration Date": "Expiration Date",
  "Repeal Date": "Repeal Date",
  "Legal Status Date": "Legal Status Date",
  "Change Date": "Change Date",
  Volume: "Volume",
  "Authorized Body": "Authorized Body",
  Obligated: "Obligated",
  "Released By": "Released By",
  Comments: "Comments",
  References: "References",
  references: "references",
  "No references found for this act.": "No references found for this act.",
  "No ELI ID": "No ELI ID",
  Texts: "Texts",
  "Text Versions": "Text Versions",
  "Full Act PDF": "Full Act PDF",
  "Consolidated document": "Consolidated document",
  "No text files available for this act.":
    "No text files available for this act.",
  "Consolidated text": "Consolidated text",
  "Published text": "Published text",
  "Uniform text": "Uniform text",
  "Historical text": "Historical text",
  "Initial text": "Initial text",
  Text: "Text",
  Directives: "Directives",
  "EU Directives": "EU Directives",
  "No EU directives linked to this act.":
    "No EU directives linked to this act.",
  Prints: "Prints",
  "Parliamentary Prints": "Parliamentary Prints",
  "No parliamentary prints linked to this act.":
    "No parliamentary prints linked to this act.",
  Print: "Print",
  Term: "Term",
  View: "View",
  "Full Text": "Full Text",
  "Untitled Act": "Untitled Act",
  Title: "Title",
  "Download PDF": "Download PDF",
  "Official PDF": "Official PDF",
  "Open in new tab": "Open in new tab",
  "PDF Preview": "PDF Preview",
  "Sejm API": "Sejm API",
  "No full text available for this act.":
    "No full text available for this act.",
  "Full text not available. See PDF preview.":
    "Full text not available. See PDF preview.",
  Structure: "Structure",
  "Search provisions...": "Search provisions...",
  "Semantic search acts...": "Semantic search acts...",
  "Search by title": "Search by title",
  "Search provisions": "Search provisions",
  "Articles indexed": "Articles indexed",
  "provisions in": "provisions in",
  provisions: "provisions",
  "results for": "results for",
  "Tool output format": "Tool output format",
  "Raw JSON output": "Raw JSON output",
  Any: "Any",
  "Exact phrase filter": "Exact phrase filter",
  Book: "Book",
  Part: "Part",
  Branch: "Branch",
  Chapter: "Chapter",
  "Sub-chapter": "Sub-chapter",
  "Art.": "Art.",
  "§": "§",
  "ust.": "sec.",
  pkt: "pt",
  "lit.": "let.",
  "Search files and folders...": "Search files and folders...",
  "No results found": "No results found",
  "Attach to project": "Attach to project",
  "Detach from project": "Detach from project",

  // Onboarding
  "onboarding.welcome.title": "Welcome to AnyLawyer!",
  "onboarding.welcome.content":
    "Let us show you around. This quick tour will help you get started with analyzing legal documents.",
  "onboarding.askQuestion.title": "Ask Questions",
  "onboarding.askQuestion.content":
    "Type your legal question here. Our AI will analyze your documents and provide relevant answers with citations.",
  "onboarding.improvePrompt.title": "Improve Your Question",
  "onboarding.improvePrompt.content":
    "Click here to automatically rewrite your question to be clearer and more effective for better results.",
  "onboarding.projectSelector.title": "Select a Project",
  "onboarding.projectSelector.content":
    "Choose an existing project or create a new one. Projects help organize your documents and conversations.",
  "onboarding.createProject.title": "Create a New Project",
  "onboarding.createProject.content":
    "Start a new project here. Give it a name and begin uploading your legal documents.",
  "onboarding.projects.title": "Your Projects",
  "onboarding.projects.content":
    "Here's where you'll find all your projects. Click on any project to open it and continue your work. You're all set!",

  // Project Onboarding
  "onboarding.project.welcome.title": "Project View",
  "onboarding.project.welcome.content":
    "This is your project workspace. Here you can organize documents, manage conversations, and get AI-powered answers.",
  "onboarding.project.upload.title": "Upload Documents",
  "onboarding.project.upload.content":
    "Drag and drop files here or click to upload your legal documents. Supported formats include PDF, Word, and more.",
  "onboarding.project.legalDocs.title": "Legal Documents Library",
  "onboarding.project.legalDocs.content":
    "Access our curated library of legal documents. Select the ones relevant to your project for AI analysis.",
  "onboarding.project.context.title": "Project Context",
  "onboarding.project.context.content":
    "Add a description to provide context for the AI. This helps generate more accurate and relevant answers.",
  "onboarding.project.topics.title": "Conversation Topics",
  "onboarding.project.topics.content":
    "Your conversations are organized into topics. Each topic keeps a separate thread of questions and answers.",
  "onboarding.project.chat.title": "Ask Questions",
  "onboarding.project.chat.content":
    "Type your question here to start a new conversation. The AI will analyze your project documents to provide answers.",
  "onboarding.project.settings.title": "Response Settings",
  "onboarding.project.settings.content":
    "Adjust reasoning depth and response length. Higher reasoning provides more thorough analysis.",
  "onboarding.project.googleDrive.title": "Google Drive Integration",
  "onboarding.project.googleDrive.content":
    "Connect your Google Drive to access and analyze documents directly from your cloud storage.",
  "onboarding.project.eureka.title": "Tax Interpretations (Eureka)",
  "onboarding.project.eureka.content":
    "This option is selectable. Enable it to give AI access to the full Eureka tax interpretations database in this project.",

  // Documentation
  Documentation: "Documentation",
  "Welcome to the AnyLawyer documentation":
    "Welcome to the AnyLawyer documentation",
  "AnyLawyer documentation helps you set up and master our AI-powered legal document intelligence platform. AnyLawyer extracts key data, answers document questions with grounded citations, generates summaries and reports, analyzes court transcripts, and surfaces insights with deep search.":
    "AnyLawyer documentation helps you set up and master our AI-powered legal document intelligence platform. AnyLawyer extracts key data, answers document questions with grounded citations, generates summaries and reports, analyzes court transcripts, and surfaces insights with deep search.",

  // Audit Log
  "Audit Log": "Audit Log",
  "Activity log for your organization": "Activity log for your organization",
  "Track all user actions and changes across your organization":
    "Track all user actions and changes across your organization",
  "All actions": "All actions",
  "All users": "All users",
  "All organizations": "All organizations",
  "Clear filters": "Clear filters",
  "audit.document_upload": "Document uploaded",
  "audit.document_rename": "Document renamed",
  "audit.document_delete": "Document deleted",
  "audit.project_create": "Project created",
  "audit.project_archive": "Project archived",
  "audit.project_unarchive": "Project restored",
  "audit.project_rename": "Project renamed",
  "audit.project_delete": "Project deleted",
  "audit.project_change_visibility": "Project visibility changed",
  "audit.project_update_description": "Project description updated",
  "audit.topic_create": "Topic created",
  "audit.topic_rename": "Topic renamed",
  "audit.topic_delete": "Topic deleted",
  "audit.message_send": "Message sent",
  "No audit logs found": "No audit logs found",
  Action: "Action",
  Resource: "Resource",
  Timestamp: "Timestamp",
  Details: "Details",
  "Rows per page": "Rows per page",
  entries: "entries",
  in: "in",
};

// Polish translations
const pl = {
  // I use it to no loose typing when calling t() function but also to be able to call it on arbitrary validation errors
  __generic: "__generic",

  Language: "Język",
  Save: "Zapisz",
  Delete: "Usuń",
  Restore: "Przywróć",
  Back: "Wstecz",
  None: "Brak",

  // Auth
  // Projects
  "Add project": "Nowy projekt",
  "New project": "Nowy projekt",
  "Delete project": "Usuń projekt",
  "Archive project": "Archiwizuj projekt",
  "Restore project": "Przywróć projekt",
  "Archived projects": "Zarchiwizowane projekty",
  "Archive nav": "Archiwum",
  "Search archived projects...": "Szukaj zarchiwizowanych projektów...",
  "Project archived": "Projekt został zarchiwizowany",
  "Project restored": "Projekt został przywrócony",
  "Project name": "Nazwa projektu",
  Projects: "Projekty",
  "Search projects...": "Szukaj projektów...",
  "No projects found": "Nie znaleziono projektów",
  "No projects yet": "Brak projektów",
  "Create a project to start uploading documents and asking questions.":
    "Utwórz projekt, aby zacząć dodawać dokumenty i zadawać pytania.",
  Topic: "Temat",
  Document: "Dokument",
  "Show only my projects": "Pokaż tylko moje projekty",
  "Filter projects": "Filtruj projekty",
  Question: "Pytanie",
  "Project with that name already exists": "Projekt o tej nazwie już istnieje",
  "Project with that name already exists, including archived projects":
    "Projekt o tej nazwie już istnieje, także w archiwum",
  "Name should have at least 3 letters":
    "Nazwa powinna mieć przynajmniej 3 litery",
  "Name cannot be empty": "Nazwa nie może być pusta",
  source: "źrodło",
  "Are you sure you want to delete this project?":
    "Czy na pewno chcesz usunąć ten projekt?",
  "Project settings": "Ustawienia projektu",
  "Private project": "Prywatny projekt",
  Private: "Prywatny",
  Shared: "Udostępniony",
  Public: "Publiczny",
  "Only you can access this project": "Tylko Ty masz dostęp do tego projektu",
  "Who can access": "Kto ma dostęp",
  "Only me": "Tylko ja",
  "Specific people": "Wybrane osoby",
  "Everyone in organization": "Wszyscy w organizacji",
  "Shared with specific people": "Udostępniony wybranym osobom",
  "Add member...": "Dodaj członka...",
  "Project members": "Członkowie projektu",
  "No members yet": "Brak członków",
  "Deleted user": "Usunięty użytkownik",
  Owner: "Właściciel",
  "Project description": "Opis projektu",
  "Add a description...": "Dodaj opis...",
  "Click to add a description for this project...":
    "Kliknij, aby dodać opis projektu...",
  "No project description.": "Brak opisu projektu.",
  "Start a new conversation...": "Rozpocznij nową rozmowę...",
  "Restore to start a new conversation":
    "Przywróć projekt, aby rozpocząć nową rozmowę",
  "Restore project to continue this conversation":
    "Przywróć projekt, aby kontynuować tę rozmowę",
  "This project is archived": "Ten projekt jest zarchiwizowany",
  "Archived on {{date}}. You can still review its content, but editing, uploads, and new conversations are disabled.":
    "Zarchiwizowano {{date}}. Nadal możesz przeglądać zawartość projektu, ale edycja, przesyłanie plików i nowe rozmowy są wyłączone.",
  "Archived {{time}}": "Zarchiwizowano {{time}}",
  "Archive this project? It will be removed from active lists, remain viewable, and can be restored later.":
    "Czy zarchiwizować ten projekt? Zniknie z aktywnych list, pozostanie dostępny do podglądu i będzie można go później przywrócić.",
  "Archived projects stay viewable but are removed from active workflows until restored.":
    "Zarchiwizowane projekty pozostają dostępne do podglądu, ale nie biorą udziału w aktywnej pracy do czasu przywrócenia.",
  "No archived projects": "Brak zarchiwizowanych projektów",
  "Archive a project to move it out of your active list.":
    "Zarchiwizuj projekt, aby usunąć go z aktywnej listy.",
  "Try a different search term.": "Spróbuj innej frazy.",
  You: "Ty",

  // Documents
  "Attach documents": "Załącz dokumenty",
  "Delete document": "Usuń dokument",
  "Failed to delete document": "Nie udało się usunąć dokumentu",
  Documents: "Dokumenty",
  "Upload documents": "Prześlij dokumenty",
  "Uploading...": "Przesyłanie...",
  "Processing...": "Przetwarzanie...",
  "Uploading {{file}} failed": "Przesyłanie {{file}} nie powiodło się",
  "Uploaded {{time}}": "Przesłano {{time}}",
  "File too big": "Plik jest za duży",
  "{{file}} is too big. Max file size is {{size}}.":
    "{{file}} jest za duży. Maksymalny rozmiar pliku to {{size}}.",
  "{{file}} already exists": "{{file}} już istnieje",
  "Invalid file type": "Nieprawidłowy typ pliku",
  "{{file}} is not a supported file type.":
    "{{file}} nie jest obsługiwanym typem pliku",
  "Drop files here to upload": "Upuść pliki tutaj, aby je przesłać",
  "Read only": "Tylko do odczytu",
  "File processing failed. Remove the file and try again.":
    "Przetwarzanie pliku nie powiodło się. Usuń plik i spróbuj ponownie.",
  Error: "Błąd",
  "Document not found": "Dokument nie znaleziony",
  "Rename document": "Zmień nazwę dokumentu",
  "Document name": "Nazwa dokumentu",
  "Document extension mismatch": "Nieprawidłowe rozszerzenie dokumentu",

  // Topics
  Topics: "Tematy",
  Messages: "Wiadomości",
  "Messages limit": "Limit wiadomości",
  "Recent topics": "Ostatnie tematy",
  "New topic": "Nowy temat",
  "Created {{time}}": "Utworzono {{time}}",
  "Delete topic": "Usuń temat",
  Untitled: "Bez nazwy",
  "Topic name": "Nazwa tematu",
  "Rename topic": "Zmień nazwę tematu",

  // Chat related
  "Thank you for your feedback!": "Dziękujemy za opinię!",
  "We'll use this to improve our responses.":
    "Wykorzystamy ją do ulepszenia naszych odpowiedzi.",
  "Bad answer": "Zła odpowiedź",
  "Good answer": "Dobra odpowiedź",
  "We're glad this response was helpful.":
    "Cieszymy się, że ta odpowiedź była pomocna.",
  "Message copied to clipboard": "Skopiowano wiadomość do schowka",
  "Searching documents": "Przeszukuję dokumenty",
  "Searching sources...": "Przeszukuję źródła...",
  "Source search complete": "Przeszukano źródła",
  "Searching legal provisions...": "Przeszukuję przepisy...",
  "Provisions search complete": "Przeszukano przepisy",
  "Searching tax interpretations...": "Przeszukuję interpretacje podatkowe...",
  "Tax interpretations search complete": "Przeszukano interpretacje podatkowe",
  "Searching procurement notices...":
    "Przeszukuję ogłoszenia zamówień publicznych...",
  "Procurement notices search complete":
    "Wyszukiwanie ogłoszeń zamówień zakończone",
  "Reading provision...": "Wczytuję przepis...",
  "Provision loaded": "Przepis wczytany",
  "Looking up act...": "Wyszukuję akt...",
  "Act details loaded": "Szczegóły aktu wczytane",
  "Tracing amendments...": "Śledzę nowelizacje...",
  "Amendment chain loaded": "Łańcuch nowelizacji wczytany",
  "No relevant provisions found": "Nie znaleziono odpowiednich przepisów",
  Context: "Kontekst",
  "Searching Supreme Court rulings...":
    "Wyszukuję orzeczenia Sądu Najwyższego...",
  "Supreme Court search complete":
    "Wyszukiwanie orzeczeń Sądu Najwyższego zakończone",
  "Supreme Court ruling": "Orzeczenie SN",
  "Source document": "Dokument źródłowy",
  "Open source document": "Otwórz dokument źródłowy",
  "Inline preview is not available for this source.":
    "Podgląd w panelu nie jest dostępny dla tego źródła.",
  "Unable to load judgment content.":
    "Nie udało się załadować treści orzeczenia.",
  "Case number": "Sygnatura",
  Court: "Sąd",
  Division: "Wydział",
  "Judgment date": "Data orzeczenia",
  "Judgment type": "Rodzaj orzeczenia",
  Judges: "Sędziowie",
  Content: "Treść",
  "Researching Polish law...": "Analizuję polskie prawo...",
  "Polish law research complete": "Analiza polskiego prawa zakończona",
  "Connecting to legal database": "Łączę z bazą aktów prawnych",
  "Searching Polish acts": "Szukam polskich aktów",
  "Reading act details": "Czytam szczegóły aktu",
  "Synthesizing legal findings": "Składam ustalenia prawne",
  "Polish law agent error": "Błąd agenta prawa polskiego",
  "No subagent activity yet": "Brak działań podagenta",
  "MCP endpoint": "Endpoint MCP",
  "Tool calls count": "Wywołania narzędzi: {{count}}",
  "Tool call identifier": "Identyfikator wywołania",
  "Recent actions": "Ostatnie działania",
  Warnings: "Ostrzeżenia",
  "No legal findings available": "Brak ustaleń prawnych do pokazania",
  Citation: "Cytat",
  "LegalSource.SEJM": "Sejm ELI (DU/MP)",
  "LegalSource.SEJM.description":
    "Wyszukuj akty z przestrzeni Sejm ELI (Dziennik Ustaw, Monitor Polski)",
  "LegalSource.Eureka": "Interpretacje podatkowe (Eureka)",
  "LegalSource.Eureka.description":
    "Wyszukuj polskie interpretacje podatkowe z bazy Eureka",
  "LegalSource.EURegulations": "Regulacje UE",
  "LegalSource.EURegulations.description":
    "Wyszukuj regulacje UE i wymagania zgodności (GDPR, NIS2, DORA, AI Act i inne)",
  "LegalSource.BZP": "Ogłoszenia zamówień (BZP/TED)",
  "LegalSource.BZP.description":
    "Wyszukuj ogłoszenia zamówień publicznych z BZP/TED wraz ze szczegółami ogłoszeń",
  "LegalSource.KRSCurrent": "KRS (odpis aktualny)",
  "LegalSource.KRSCurrent.description":
    "Pobierz aktualne dane podmiotu z KRS po numerze KRS",
  "LegalSource.KRSFull": "KRS (odpis pełny)",
  "LegalSource.KRSFull.description":
    "Pobierz pełne dane historyczne podmiotu z KRS po numerze KRS",
  "LegalSource.DU": "Dziennik Ustaw (DU)",
  "LegalSource.DU.description":
    "Wyszukuj w aktach prawnych obowiązujących na dany dzień",
  "LegalSource.SN": "Sąd Najwyższy (sn.pl)",
  "LegalSource.SN.description": "Wyszukuj orzeczenia z sn.pl",
  "LegalSource.SP": "Sądy powszechne (orzeczenia.ms.gov.pl)",
  "LegalSource.SP.description": "Wyszukuj orzeczenia z orzeczenia.ms.gov.pl",
  "LegalSource.IP": "Interpretacje podatkowe (IP)",
  "LegalSource.IP.description": "Wyszukuj interpretacje podatkowe",
  "LegalSource.TK": "Trybunał Konstytucyjny (ipo.trybunal.gov.pl)",
  "LegalSource.TK.description": "Wyszukuj orzeczenia z ipo.trybunal.gov.pl",
  "LegalSource.KIO": "Krajowa Izba Odwoławcza (wokanda.uzp.gov.pl)",
  "LegalSource.KIO.description": "Wyszukuj orzeczenia z wokanda.uzp.gov.pl",
  "Searching common court rulings...":
    "Wyszukiwanie orzeczeń sądów powszechnych...",
  "Common courts search complete":
    "Wyszukiwanie orzeczeń sądów powszechnych zakończone",
  "Searching Constitutional Tribunal rulings...":
    "Wyszukiwanie orzeczeń Trybunału Konstytucyjnego...",
  "Constitutional Tribunal search complete":
    "Wyszukiwanie orzeczeń Trybunału Konstytucyjnego zakończone",
  "Searching National Appeal Chamber rulings...":
    "Wyszukiwanie orzeczeń Krajowej Izby Odwoławczej...",
  "National Appeal Chamber search complete":
    "Wyszukiwanie orzeczeń Krajowej Izby Odwoławczej zakończone",
  "Open in SAOS": "Otwórz w SAOS",
  "Relevant content found": "Przeszukano dokumenty",
  "No relevant documents found": "Nie znaleziono pasujących dokumentów",
  "Analyzing...": "Analizuję...",
  "Stop generation": "Zatrzymaj generowanie",
  Page: "Strona",
  "Copy message": "Kopiuj wiadomość",
  "No topics yet": "Brak tematów",
  "Calculating...": "Obliczanie...",
  "Define your prompt...": "Dodaj swój prompt...",
  "Prompt name": "Nazwa prompta",
  "Save as .docx": "Zapisz jako .docx",
  "AnyLawyer - Answer.docx": "AnyLawyer - Odpowiedź.docx",
  "Message is too long ({{count}}/{{max}}). Remove {{over}} characters.":
    "Wiadomość jest za długa ({{count}}/{{max}}). Usuń {{over}} znaków.",

  // Empty states
  "Create your first topic to start the conversation":
    "Utwórz pierwszy temat, aby rozpocząć rozmowę",

  // Analysis and reflection states
  "Analysis complete": "Analiza zakończona",
  "Reflecting on the information...": "Badanie informacji...",

  "Generating citations": "Generuję cytaty",

  // Context-aware loading states
  "Processing your question": "Przetwarzam pytanie",
  "Reading the results": "Czytam wyniki",
  "Analyzing the results": "Analizuję wyniki",
  "Thinking about the question": "Zastanawiam się nad pytaniem",
  "Gathering information": "Zbieranie informacji",
  "Reviewing findings": "Przeglądam wyniki",
  "Formulating answer": "Formułuję odpowiedź",
  "Cross-referencing sources": "Sprawdzam źródła",
  "Verifying information": "Weryfikuję informacje",
  "Building comprehensive response": "Tworzę szczegółową odpowiedź",

  "Comparison complete": "Porównanie zakończone",
  "Comparing documents...": "Porównuję dokumenty...",
  Preview: "Podgląd",

  // Error pages
  "Lost in the legal maze?": "Zgubiony w prawnym labiryncie?",
  "The page you're looking for doesn't exist or has been moved.":
    "Strona, której szukasz, nie istnieje lub została przeniesiona.",
  "Go back to main page": "Wróć do strony głównej",
  "Something went wrong": "Coś poszło nie tak",
  "An unexpected error occurred. Please try again.":
    "Wystąpił nieoczekiwany błąd. Spróbuj ponownie.",

  // New dashboard translations
  "What can I help you with today?": "W czym mogę Ci dzisiaj pomóc?",
  "Ask questions about your legal documents or start a new conversation":
    "Zadawaj pytania o swoje dokumenty prawne lub rozpocznij nową rozmowę",
  docs: "dok.",
  "Ask anything about your legal documents...":
    "Zapytaj o cokolwiek dotyczące swoich dokumentów prawnych...",
  documents: "dokumenty",
  "Select project": "Wybierz projekt",
  // Document tabs and search
  "Search documents...": "Szukaj dokumentów...",
  Project: "Dokumenty Projektu",
  Organization: "Dokumenty Organizacji",
  "Legal documents": "Dokumenty prawne",
  "Organization Documents": "Dokumenty organizacji",
  "No documents found": "Nie znaleziono dokumentów",
  "Added {{time}}": "Dodano {{time}}",
  "Other organization members will not be able to see this project":
    "Inne członkowie organizacji nie będą mogli widzieć tego projektu",
  page: "strona",
  pages: "stron",

  // Universal document picker
  "Choose documents from your library. Hover over items to preview content.":
    "Wybierz dokumenty z biblioteki. Najedź na elementy, aby zobaczyć podgląd treści.",
  "Choose documents from your organization. Hover over items to preview content.":
    "Wybierz dokumenty z organizacji. Najedź na elementy, aby zobaczyć podgląd treści.",
  document: "dokument",
  "No documents available": "Brak dostępnych dokumentów",
  "Select or hover over a document to preview":
    "Wybierz lub najedź na dokument, aby zobaczyć podgląd",
  Cancel: "Anuluj",
  Done: "Gotowe",
  selected: "wybrano",
  Attach: "Załącz",
  Detach: "Odłącz",
  "Add to project": "Dodaj do projektu",
  "Remove from project": "Usuń z projektu",

  // Project creation
  "Projects allow you to attach & organize your own documents and collaborate with your team.":
    "Projekty pozwalają na załączanie i organizowanie własnych dokumentów oraz współpracę z zespołem.",

  // Document selection error messages
  "Failed to update universal document selection. Please try again.":
    "Nie udało się zaktualizować wyboru dokumentu uniwersalnego. Spróbuj ponownie.",
  "Failed to update organization document selection. Please try again.":
    "Nie udało się zaktualizować wyboru dokumentu organizacji. Spróbuj ponownie.",

  // Admin Dashboard
  "Admin Tools": "Administracja",
  Integrations: "Integracje",
  "Organization Stats": "Statystyki organizacji",
  "Organization metrics and insights": "Metryki i statystyki organizacji",
  "Last 7 days": "Ostatnie 7 dni",
  "Last 30 days": "Ostatnie 30 dni",
  "Current month": "Bieżący miesiąc",
  "Previous month": "Poprzedni miesiąc",
  "Pick a date range": "Wybierz zakres dat",
  "Quick selection": "Szybki wybór",
  "Users Active": "Aktywni użytkownicy",
  "Projects Created": "Utworzone projekty",
  "Documents Uploaded": "Przesłane dokumenty",
  "Topics Created": "Utworzone tematy",
  "Messages Sent": "Wysłane wiadomości",
  "Users who created projects or sent messages":
    "Użytkownicy, którzy utworzyli projekty lub wysłali wiadomości",
  "Organization ID": "ID organizacji",
  "Daily Activity": "Aktywność dzienna",
  "Topics created and user messages sent per day":
    "Tematy utworzone i wiadomości użytkowników wysłane dziennie",
  "Most Active Users": "Najbardziej aktywni użytkownicy",
  "Users with the most messages sent":
    "Użytkownicy z największą liczbą wysłanych wiadomości",
  User: "Użytkownik",
  "Show More": "Pokaż więcej",
  "Show Less": "Pokaż mniej",
  more: "więcej",
  "Turbopuffer Namespaces": "Przestrzenie nazw Turbopuffer",
  "Top 50 biggest Turbopuffer namespaces and linked projects.":
    "50 największych przestrzeni nazw Turbopuffer i powiązane projekty.",
  Namespace: "Przestrzeń nazw",
  Size: "Rozmiar",
  Rows: "Wiersze",
  "Last accessed": "Ostatni dostęp",

  // Model settings
  "Model settings": "Ustawienia modelu",
  Model: "Model",
  Reasoning: "Rozumowanie",
  Verbosity: "Szczegółowość",

  // Reasoning levels
  "reasoning.None": "Szybkie",
  "reasoning.Medium": "Standardowe",
  "reasoning.High": "Dogłębne",

  // Verbosity levels
  "verbosity.Low": "Zwięźle",
  "verbosity.Medium": "Normalnie",
  "verbosity.High": "Szczegółowo",

  // Reasoning effort descriptions
  "reasoning.none": "Błyskawiczne odpowiedzi",
  "reasoning.medium": "Dlugie przetwarzanie, zbalansowane rozumowanie",
  "reasoning.high": "Inteligencja klasy badawczej",

  // Verbosity descriptions
  "verbosity.low": "Zwięzłe odpowiedzi",
  "verbosity.medium": "Umiarkowane wyjaśnienia",
  "verbosity.high": "Szczegółowe i dokładne omówienia",

  // Model selection
  // Experimental model notice

  // Voice input
  "Voice input": "Wpisz głosem",
  "Stop voice input": "Zatrzymaj wpisywanie głosem",
  "Listening...": "Słucham...",
  "Transcribing...": "Transkrybuję...",
  "Click to stop listening": "Kliknij, aby przestać słuchać",
  "Click to start voice input": "Kliknij, aby wpisywać głosem",
  "Transcribing your voice...": "Transkrybuję Twój głos...",

  // Web browsing
  "Web browsing": "Przeglądanie internetu",
  "Enable web browsing to search the internet":
    "Włącz przeglądanie internetu, aby przeszukać sieć",
  "Enable {{source}}": "Włącz {{source}}",
  "Searching the web...": "Przeszukuję internet...",
  "Web search complete": "Przeszukano internet",
  "Reading web results": "Czytam wyniki z internetu",
  "Web page link": "Link do strony",
  "Failed to open web page": "Nie udało się otworzyć strony",
  "Expand details": "Pokaż szczegóły",
  "Collapse details": "Ukryj szczegóły",

  // Google Drive
  "Google Drive": "Google Drive",
  Connect: "Połącz",
  Connected: "Połączono",
  Disconnect: "Rozłącz",
  "Are you sure you want to disconnect Google Drive?":
    "Czy na pewno chcesz rozłączyć Google Drive?",
  "AI can search your Google Drive files":
    "AI może przeszukiwać pliki na Google Drive",
  "Searching Google Drive...": "Przeszukuję Google Drive...",
  "Google Drive search complete": "Przeszukano Google Drive",
  "Opening file from Google Drive...": "Otwieram plik z Google Drive...",
  "Google Drive link": "Link z Google Drive",
  "Connect Google Drive to continue": "Połącz Google Drive, aby kontynuować",
  "We need access to open this Google Drive link.":
    "Potrzebujemy dostępu, aby otworzyć ten link z Google Drive.",
  "We couldn't open that Google Drive link":
    "Nie udało się otworzyć tego linku z Google Drive",
  "File opened successfully": "Plik otwarty pomyślnie",
  "Failed to open file": "Nie udało się otworzyć pliku",
  "Open in Google Docs": "Otwórz w Google Docs",
  "Open in Google Sheets": "Otwórz w Google Sheets",
  Found: "Znaleziono",
  files: "plików",
  "No files found in Google Drive": "Nie znaleziono plików w Google Drive",
  "Google Drive (entire)": "Google Drive (cały)",
  "Include entire Google Drive": "Uwzględnij cały Google Drive",
  "Search across all accessible files (My Drive + Shared Drives).":
    "Przeszukuj wszystkie dostępne pliki (Mój dysk + dyski współdzielone).",
  "Whole drive enabled": "Włączono cały dysk",
  "Disable whole-drive search to attach folders or files.":
    "Wyłącz przeszukiwanie całego dysku, aby załączać foldery lub pliki.",

  // OneDrive
  OneDrive: "OneDrive",
  "Are you sure you want to disconnect OneDrive?":
    "Czy na pewno chcesz rozłączyć OneDrive?",
  "AI can search your OneDrive files": "AI może przeszukiwać pliki na OneDrive",
  "Connect OneDrive to attach folders": "Połącz OneDrive, aby załączyć foldery",
  "Your OneDrive connection has expired.":
    "Twoje połączenie z OneDrive wygasło.",
  "Searching OneDrive...": "Szukam w OneDrive...",
  "OneDrive search complete": "Wyszukiwanie w OneDrive zakończone",
  "No files found in OneDrive": "Nie znaleziono plików w OneDrive",
  "Opening file from OneDrive...": "Otwieram plik z OneDrive...",

  // Prompt Improvement
  "Improve prompt": "Popraw prompt",
  "Improving prompt...": "Przepisuję...",
  "Rewrite your question to be clearer and more effective":
    "Przepisz pytanie, aby było jaśniejsze i bardziej efektywne",

  // Read document pages tool
  "Reading document pages...": "Czytam strony dokumentu...",
  "Document pages loaded": "Załadowano strony dokumentu",
  Pages: "Strony",

  // Dropbox
  // Connected Sources
  "Connected Sources": "Połączone źródła",

  // Cloud Drives
  "Connect Google Drive to attach folders":
    "Połącz Google Drive, aby załączyć foldery",
  "Failed to update folder attachment. Please try again.":
    "Nie udało się zaktualizować załączenia folderu. Spróbuj ponownie.",
  "Limit reached": "Osiągnięto limit",
  "You can attach up to 10 folders or files. Please detach some items first.":
    "Możesz załączyć maksymalnie 10 folderów lub plików. Najpierw odłącz niektóre elementy.",
  "You can attach up to 25 folders or files. Please detach some items first.":
    "Możesz załączyć maksymalnie 25 folderów lub plików. Najpierw odłącz niektóre elementy.",
  "Monthly message limit reached. Contact your administrator.":
    "Osiągnięto limit wiadomości. Skontaktuj się z administratorem.",

  // Google OAuth Settings
  "Google OAuth Configuration": "Konfiguracja Google OAuth",
  "Configure organization-specific Google OAuth credentials for Google Drive integration.":
    "Skonfiguruj dane OAuth Google dla integracji z Google Drive dla organizacji.",
  "Google OAuth Client ID": "ID Klienta Google OAuth",
  "Enter your Google OAuth Client ID": "Wprowadź ID Klienta Google OAuth",
  "Google OAuth Client Secret": "Tajny klucz klienta Google OAuth",
  "Enter your Google OAuth Client Secret":
    "Wprowadź tajny klucz klienta Google OAuth",
  "Save OAuth Settings": "Zapisz ustawienia OAuth",
  "Setup Instructions": "Instrukcje konfiguracji",
  "and create a new project": "i utwórz nowy projekt",
  "and create OAuth 2.0 Client ID (Web application type)":
    "i utwórz OAuth 2.0 Client ID (typ Aplikacja webowa)",
  "Add authorized redirect URI:": "Dodaj autoryzowany URI przekierowania:",
  "Copy the Client ID and Client Secret to the form above":
    "Skopiuj ID Klienta i Tajny klucz klienta do formularza powyżej",
  "Connect Google Drive using the organization's credentials":
    "Połącz Google Drive używając danych organizacji",
  "Important:": "Ważne:",
  "Google Picker API Key": "Klucz API Google Picker",
  "Enter your Browser API Key (optional)":
    "Wprowadź klucz API przeglądarki (opcjonalnie)",
  "Picker API key is configured. Leave empty to keep unchanged.":
    "Klucz API Picker jest skonfigurowany. Pozostaw puste, aby nie zmieniać.",
  "Optional - required for the Google Picker file selection flow. Create a Browser API key in your Google Cloud Console credentials.":
    "Opcjonalne — wymagane dla wyboru plików przez Google Picker. Utwórz klucz API przeglądarki w ustawieniach Google Cloud Console.",
  "(Optional) Enable the Google Picker API and create a Browser API key for Picker-based file selection":
    "(Opcjonalnie) Włącz Google Picker API i utwórz klucz API przeglądarki do wyboru plików",

  // Google Drive Picker
  "Select files from Google Drive": "Wybierz pliki z Google Drive",
  "Connect Google Drive to select files":
    "Połącz Google Drive, aby wybrać pliki",
  "No files selected yet.": "Nie wybrano jeszcze plików.",
  "Already attached": "Już załączone",
  "The selected files are already attached.": "Wybrane pliki są już załączone.",
  "You can attach up to 10 files. Please detach some items first.":
    "Możesz załączyć maksymalnie 10 plików. Najpierw odłącz niektóre elementy.",
  "You can attach up to 25 files. Please detach some items first.":
    "Możesz załączyć maksymalnie 25 plików. Najpierw odłącz niektóre elementy.",
  "Google Picker is still loading. Please try again.":
    "Google Picker wciąż się ładuje. Spróbuj ponownie.",
  "Your Google Drive connection has expired.":
    "Twoje połączenie z Google Drive wygasło.",
  "Failed to load picker configuration.":
    "Nie udało się załadować konfiguracji wyboru plików.",
  "Failed to open file picker.": "Nie udało się otworzyć wyboru plików.",
  "Failed to update file attachment. Please try again.":
    "Nie udało się zaktualizować załączenia pliku. Spróbuj ponownie.",
  Reconnect: "Połącz ponownie",

  // Microsoft OAuth Settings (OneDrive)
  "Microsoft OAuth Configuration": "Konfiguracja Microsoft OAuth",
  "Configure organization-specific Microsoft OAuth credentials for OneDrive integration.":
    "Skonfiguruj dane Microsoft OAuth dla integracji z OneDrive dla organizacji.",
  "OneDrive OAuth configured": "OneDrive OAuth skonfigurowane",
  "Configuration required": "Wymagana konfiguracja",
  "Microsoft OAuth Client ID": "ID klienta Microsoft OAuth",
  "Enter your Microsoft OAuth Client ID": "Wprowadź ID klienta Microsoft OAuth",
  "Microsoft OAuth Client Secret": "Tajny klucz klienta Microsoft OAuth",
  "Enter your Microsoft OAuth Client Secret":
    "Wprowadź tajny klucz klienta Microsoft OAuth",
  "Use this exact URI in your Microsoft app registration":
    "Użyj dokładnie tego URI w rejestracji aplikacji Microsoft",
  "Saved URI differs from current environment:":
    "Zapisany URI różni się od obecnego środowiska:",
  "Both Client ID and Client Secret are required":
    "Wymagane są zarówno ID klienta, jak i tajny klucz klienta",
  "Failed to save OAuth credentials": "Nie udało się zapisać danych OAuth",
  "Microsoft OAuth credentials saved successfully":
    "Dane Microsoft OAuth zapisane pomyślnie",
  "Failed to save credentials": "Nie udało się zapisać danych",
  "Redirect URI:": "Redirect URI:",
  Success: "Sukces",

  // Organization settings
  Organizations: "Organizacje",
  "Configure AI model settings for each organization":
    "Konfiguruj ustawienia modelu AI dla każdej organizacji",
  "No organizations found": "Nie znaleziono organizacji",
  "Model updated": "Model zaktualizowany",
  "Organization model has been updated successfully.":
    "Model organizacji został pomyślnie zaktualizowany.",
  "Gemini 3.1 Pro": "Gemini 3.1 Pro",
  Custom: "Niestandardowy",
  Default: "Domyślny",
  "Saving...": "Zapisywanie...",
  Status: "Status",
  members: "członków",
  pending: "oczekujące",
  "No members": "Brak członków",
  Unknown: "Nieznany",
  "Pending invitations": "Oczekujące zaproszenia",
  Invite: "Zaproś",
  "Invite user": "Zaproś użytkownika",
  "Send an invitation to join": "Wyślij zaproszenie do",
  "Email address": "Adres email",
  Role: "Rola",
  Member: "Członek",
  Members: "Członkowie",
  Users: "Użytkownicy",
  "Drive credentials": "Dane dostępu do dysków",
  "Google Drive credentials": "Dane Google Drive",
  "OneDrive credentials": "Dane OneDrive",
  "Google Drive credentials are configured for this organization.":
    "Dane Google Drive są skonfigurowane dla tej organizacji.",
  "Google Drive credentials are not configured. Configure them in /app/google-oauth.":
    "Dane Google Drive nie są skonfigurowane. Skonfiguruj je w /app/google-oauth.",
  "OneDrive credentials are configured for this organization.":
    "Dane OneDrive są skonfigurowane dla tej organizacji.",
  "OneDrive credentials are not configured. Configure them in /app/microsoft-oauth.":
    "Dane OneDrive nie są skonfigurowane. Skonfiguruj je w /app/microsoft-oauth.",
  Admin: "Administrator",
  "Send invitation": "Wyślij zaproszenie",
  "Sending...": "Wysyłanie...",
  "Invitation sent": "Zaproszenie wysłane",
  "New organization": "Nowa organizacja",
  "Organization created": "Organizacja utworzona",
  "The organization has been created successfully.":
    "Organizacja została pomyślnie utworzona.",
  "Access Revoked": "Okres próbny dobiegł końca",
  "Your organization's trial access has been revoked. Please contact us to discuss further details and continue using the platform.":
    "Okres próbny Twojej organizacji dobiegł końca. Skontaktuj się z nami, aby omówić szczegóły i kontynuować korzystanie z platformy.",
  "Contact Us": "Skontaktuj się z nami",
  "Access status updated": "Status dostępu zaktualizowany",
  "Organization trial access status has been updated.":
    "Status dostępu próbnego organizacji został zaktualizowany.",
  "Create a new organization": "Utwórz nową organizację",
  "Organization name": "Nazwa organizacji",
  "Enter organization name": "Wprowadź nazwę organizacji",
  "Verified domain": "Zweryfikowana domena",
  "Enter verified domain (e.g. example.com)":
    "Wpisz zweryfikowaną domenę (np. example.com)",
  "Users signing up with this email domain will be invited automatically.":
    "Użytkownicy rejestrujący się z tym adresem email otrzymają automatyczne zaproszenie.",
  "Creating...": "Tworzenie...",
  "Create organization": "Utwórz organizację",
  "messages ({{period}})": "wiadomości ({{period}})",
  "The invitation has been sent successfully.":
    "Zaproszenie zostało pomyślnie wysłane.",
  "An error occurred.": "Wystąpił błąd.",
  "Msg/User Limit": "Limit wiad./użytk.",
  "Msg/Org Limit": "Limit wiad./org.",
  "No limit": "Bez limitu",
  "Limit updated": "Limit zaktualizowany",
  "Messages per user limit has been updated successfully.":
    "Limit wiadomości na użytkownika został pomyślnie zaktualizowany.",
  "Messages per organization limit has been updated successfully.":
    "Limit wiadomości na organizację został pomyślnie zaktualizowany.",
  // Transcriptions
  Transcriptions: "Transkrypcje",
  "Transcribe audio files": "Transkrybuj pliki audio",
  "New Transcription": "Nowa transkrypcja",
  "Upload Audio": "Prześlij audio",
  "No transcriptions yet": "Brak transkrypcji",
  "Upload your first audio file to get started":
    "Prześlij pierwszy plik audio, aby rozpocząć",
  "Failed to delete transcription": "Nie udało się usunąć transkrypcji",
  "Transcription uploaded": "Transkrypcja przesłana",
  "Your audio file is being processed": "Twój plik audio jest przetwarzany",
  "Processing transcription...": "Przetwarzanie transkrypcji...",
  "Transcription processed": "Transkrypcja przetworzona",
  "Transcription failed": "Transkrypcja nie powiodła się",
  Transcript: "Transkrypt",
  "Audio file": "Plik audio",
  "Supported formats: MP3, WAV":
    "Obsługiwane formaty: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM",
  "Unsupported audio format":
    "{{filename}} nie jest obsługiwanym formatem audio. Obsługiwane: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM.",
  "Convert to document": "Konwertuj na dokument",
  "Make this transcript searchable in your projects":
    "Uczyń transkrypt przeszukiwalnym w projektach",
  "Converted {{time}}": "Skonwertowano {{time}}",
  "Converting...": "Konwertowanie...",
  "Already converted": "Już skonwertowano",
  "Transcription converted to document":
    "Transkrypcja skonwertowana na dokument",
  "Include timestamps": "Dodaj znaczniki czasu",
  Joined: "Dołączył/a",
  "Last active": "Ostatnio aktywny/a",
  Never: "Nigdy",

  // Leaflet
  "Leaflet.metaTitle": "Ulotka AnyLawyer",
  "Leaflet.metaDescription":
    "Ulotka A4 do druku opisująca AnyLawyer oraz cennik.",
  "Leaflet.logoAlt": "Logo AnyLawyer",
  "Leaflet.toolbarHint": "Podgląd A4 – otwórz okno drukowania, by zapisać PDF.",
  "Leaflet.print": "Drukuj",
  "Leaflet.title": "Legal Intelligence, Perfected.",
  "Leaflet.subtitle":
    "Bezpieczna przestrzeń AI do analizy dokumentów prawnych. Analizuj tysiące dokumentów w kilka minut. Integracja z Google Drive, OneDrive i prywatną chmurą. Polityka zero-treningu oraz odpowiedzi z cytatami opartymi o źródła.",
  "Leaflet.contactLabel": "Kontakt",
  "Leaflet.trialLabel": "Okres próbny",
  "Leaflet.trialValue": "30 dni za darmo",
  "Leaflet.whatIsTitle": "Czym jest AnyLawyer?",
  "Leaflet.whatIsBody":
    "Wgraj umowy, akta sądowe lub regulacje. Zadawaj pytania w naturalnym języku. Otrzymuj odpowiedzi z klikalnymi cytatami do konkretnych stron i paragrafów.",
  "Leaflet.aiAndLawyerTitle": "AI + prawnik",
  "Leaflet.aiCardTitle": "AI",
  "Leaflet.aiCardBody":
    "Automatyzuje przegląd, odpowiada na pytania i tworzy raporty–zawsze z oparciem o dowody.",
  "Leaflet.lawyerCardTitle": "Prawnik",
  "Leaflet.lawyerCardBody":
    "Zachowuje kontrolę: weryfikuje źródła, sprawdza tok rozumowania i doradza z pewnością.",
  "Leaflet.capabilitiesTitle": "Najważniejsze możliwości",
  "Leaflet.cap1Title": "Automatyczne due diligence",
  "Leaflet.cap1Body":
    "Wyszukuj i wyciągaj kluczowe postanowienia ze setek dokumentów z dokładnością wspartą cytatami.",
  "Leaflet.cap2Title": "Natychmiastowy research sprawy",
  "Leaflet.cap2Body":
    "Zadawaj złożone pytania całemu plikowi sprawy i otrzymuj odpowiedzi zakorzenione w dowodach.",
  "Leaflet.cap3Title": "Generowanie dokumentów",
  "Leaflet.cap3Body":
    "Twórz notatki prawne i raporty dla klienta bezpośrednio na podstawie dokumentów sprawy.",
  "Leaflet.cap7Title": "Integracja ze źródłami prawa",
  "Leaflet.cap7Body":
    "Przeszukuj orzeczenia sądów, interpretacje podatkowe i akty prawne z oficjalnych baz danych.",
  "Leaflet.pricingTitle": "Cennik",
  "Leaflet.footerTitle": "Umów demo",
  "Leaflet.footerBody":
    "Napisz do nas, aby zobaczyć AnyLawyer na Twoich dokumentach i procesach.",
  "Leaflet.tablePlan": "Plan",
  "Leaflet.tableFree": "Free (14 dni)",
  "Leaflet.tablePlus": "Plus",
  "Leaflet.tablePro": "Pro",
  "Leaflet.tablePrice": "Koszt za stanowisko/miesiąc",
  "Leaflet.tablePlusPrice": "199zł/stanowisko",
  "Leaflet.tableProPrice": "499zł/stanowisko",
  "Leaflet.tableDocuments": "Dokumenty",
  "Leaflet.tableUnlimited": "Nieograniczone",
  "Leaflet.tableMessages": "Wiadomości miesięcznie",
  "Leaflet.tableExtras": "Dodatkowo",
  "Leaflet.tableProExtras": "Transkrypcje, Google Drive/OneDrive",

  // Pricing page
  "Pricing.metaTitle": "Cennik — AnyLawyer",
  "Pricing.metaDescription": "Porównaj plany i cennik AnyLawyer.",
  "Pricing.logoAlt": "Logo AnyLawyer",
  "Pricing.pageTitle": "Cennik",
  "Pricing.saveAsPDF": "Zapisz jako PDF",
  "Pricing.headline": "Wybierz odpowiedni plan dla swojej firmy",
  "Pricing.features": "Funkcje",
  "Pricing.planFree": "Bezpłatny",
  "Pricing.planPlus": "Plus",
  "Pricing.planPro": "Pro",
  "Pricing.planEnterprise": "Enterprise",
  "Pricing.trialPeriod": "14 dni próbnych",
  "Pricing.perSeatMonth": "za stanowisko/miesiąc",
  "Pricing.free": "Bezpłatnie",
  "Pricing.custom": "Indywidualnie",
  "Pricing.monthly": "Miesięcznie",
  "Pricing.annual": "Rocznie",
  "Pricing.unlimited": "Nieograniczone",
  "Pricing.advanced": "Zaawansowane",
  "Pricing.allModels": "Wszystkie modele",
  "Pricing.allModelsBYOK": "Wszystkie + BYOK",
  "Pricing.fast": "Szybki",
  "Pricing.allModes": "Wszystkie tryby",
  "Pricing.euOrCustom": "UE lub własne",
  "Pricing.contactUs": "Skontaktuj się",
  "Pricing.categoryBasics": "Podstawowe",
  "Pricing.categoryLimits": "Limity użycia",
  "Pricing.categoryAI": "AI i modele",
  "Pricing.categoryFeatures": "Funkcje",
  "Pricing.categoryIntegrations": "Integracje",
  "Pricing.categorySecurity": "Bezpieczeństwo i zgodność",
  "Pricing.featurePrice": "Cena za stanowisko",
  "Pricing.featureBilling": "Rozliczenie",
  "Pricing.featureSeats": "Stanowiska",
  "Pricing.featureMessagesMonth": "Wiadomości miesięcznie",
  "Pricing.featureDocuments": "Dokumenty",
  "Pricing.featureProjects": "Projekty",
  "Pricing.featureAIModels": "Modele AI",
  "Pricing.featureReasoningModes": "Tryby rozumowania",
  "Pricing.featureCitations": "Cytaty ze źródeł",
  "Pricing.featureDocumentGeneration": "Generowanie dokumentów",
  "Pricing.featureTranscriptions": "Transkrypcje audio",
  "Pricing.featureDocxExport": "Eksport do DOCX",
  "Pricing.featureGoogleDrive": "Integracja z Google Drive",
  "Pricing.featureOneDrive": "Integracja z OneDrive",
  "Pricing.featureLegalSources": "Polskie źródła prawa",
  "Pricing.featureWebSearch": "Wyszukiwanie w internecie",
  "Pricing.featureAPI": "Dostęp do API",
  "Pricing.featureEncryption": "Szyfrowanie end-to-end",
  "Pricing.featureDataResidency": "Lokalizacja danych",
  "Pricing.featureZeroTraining": "Polityka zero-treningu",
  "Pricing.featureSSO": "SSO (Okta, Azure AD)",
  "Pricing.featureAuditLogs": "Logi audytowe",
  "Pricing.featurePrivateCloud": "Wdrożenie w prywatnej chmurze",
  "Pricing.ctaText": "Gotowy do rozpoczęcia?",
  "Pricing.ctaStartTrial": "Rozpocznij okres próbny",
  "Pricing.ctaContactSales": "Skontaktuj się z nami",
  "Pricing.legalSourcesFootnote":
    "obejmują: wyszukiwania w polskich aktach prawnych (ustawy, rozporządzenia), interpretacje podatkowe, orzeczenia sądów powszechnych i Sądu Najwyższego, orzeczenia Trybunału Konstytucyjnego, orzeczenia KIO (zamówienia publiczne), regulacje UE (GDPR, NIS2, DORA, AI Act i inne) oraz ogłoszenia zamówień publicznych (BZP/TED).",
  "Pricing.footerNote": "Ceny podane w PLN. VAT nie jest wliczony.",

  // Landing page - Navigation
  "Landing.nav.features": "Funkcje",
  "Landing.nav.security": "Bezpieczeństwo",
  "Landing.nav.pricing": "Cennik",
  "Landing.nav.howItWorks": "Jak to działa",
  "Landing.nav.testimonials": "Opinie",
  "Landing.nav.faq": "FAQ",
  "Landing.nav.login": "Zaloguj się",
  "Landing.nav.startTrial": "Rozpocznij za darmo",
  "Landing.nav.docs": "Dokumentacja",
  "Landing.nav.goToApp": "Przejdź do AnyLawyer",
  "Landing.nav.aboutUs": "O nas",
  "Changelog.emptyState": "Nie ma jeszcze aktualizacji produktu.",
  "Changelog.menu.emptyState": "Nie ma jeszcze aktualizacji.",
  "Changelog.menu.openAriaLabel": "Otwórz changelog",
  "Changelog.menu.whatsNew": "Nowości",
  "Changelog.menu.viewAllUpdates": "Zobacz wszystkie aktualizacje",

  // Landing page - Hero section
  "Landing.hero.badge": "Asystent AI dla prawników",
  "Landing.hero.titlePrefix": "Wzmocnij",
  "Landing.hero.titleRotating1": "swoją kancelarię",
  "Landing.hero.titleRotating2": "swój dział prawny",
  "Landing.hero.titleRotating3": "swój dział sprzedaży",
  "Landing.hero.titleSuffix": "sztuczną inteligencją.",
  "Landing.hero.description":
    "AnyLawyer to bezpieczna przestrzeń robocza AI dla prawników. Analizuj dokumenty, przeszukuj polskie akty prawne i orzecznictwo, generuj raporty – z każdą odpowiedzią opartą na źródle. Twoje dane nigdy nie służą do trenowania modeli.",
  "Landing.hero.bookDemo": "Umów demo",
  "Landing.hero.cta": "Wypróbuj za darmo – 14 dni",
  "Landing.hero.feature1": "Wbudowane polskie akty prawne",
  "Landing.hero.feature2": "Zero halucynacji – cytowania ze źródła",
  "Landing.hero.feature3": "Dane nie trenują modeli",
  "Landing.hero.feature4": "Hosting EU, GDPR",

  // Landing page - Social proof section
  "Landing.trustedBy": "Zaufali nam wiodące kancelarie prawne",
  "Landing.socialProof.badge":
    "Setki prawników korzystają z AnyLawyer codziennie",
  "Landing.socialProof.stat1.value": "100\u00A0000+",
  "Landing.socialProof.stat1.label": "odpowiedzi na pytania",
  "Landing.socialProof.stat2.value": "20\u00A0000+",
  "Landing.socialProof.stat2.label": "wgranych dokumentów",
  "Landing.socialProof.stat3.value": "50+",
  "Landing.socialProof.stat3.label": "kancelarii na pokładzie",

  // Landing page - Problem → Solution section
  "Landing.problem.title": "Bez AnyLawyer",
  "Landing.problem.item1":
    "Godziny spędzone na ręcznym przeszukiwaniu dokumentów",
  "Landing.problem.item2": "Ryzyko przeoczenia kluczowej klauzuli w umowie",
  "Landing.problem.item3":
    "ChatGPT halucynuje – nie można mu zaufać w sprawach prawnych",
  "Landing.problem.item4":
    "Brak pewności, czy odpowiedź AI jest oparta na aktualnym prawie",
  "Landing.solution.title": "Z AnyLawyer",
  "Landing.solution.item1":
    "Odpowiedź w sekundy – z dokładnym cytowaniem z dokumentu",
  "Landing.solution.item2":
    "Automatyczny przegląd tysięcy stron – nic nie umknie",
  "Landing.solution.item3":
    "Każda odpowiedź poparta źródłem – zero halucynacji",
  "Landing.solution.item4":
    "Wbudowany dostęp do Dziennika Ustaw, Monitora Polskiego i orzecznictwa",

  // Landing page - Killer Feature section
  "Landing.killerFeature.title":
    "Asystent AI z wbudowanym dostępem do polskiego prawa",
  "Landing.killerFeature.description":
    "Przeszukuj polskie źródła prawa bezpośrednio z poziomu swojego narzędzia – wszystko z jednego okna, z pełnym cytowaniem.",
  "Landing.killerFeature.microcopy":
    "Baza źródeł jest aktualizowana automatycznie. Nie musisz sprawdzać, czy dany akt jest obowiązujący – AnyLawyer robi to za Ciebie.",
  "Landing.killerFeature.source1": "Dziennik Ustaw",
  "Landing.killerFeature.source2": "Monitor Polski",
  "Landing.killerFeature.source3": "Sąd Najwyższy",
  "Landing.killerFeature.source4": "Interpretacje podatkowe",
  "Landing.killerFeature.source5": "Orzeczenia KIO",
  "Landing.killerFeature.source6": "Sądy powszechne",
  "Landing.killerFeature.source7": "Regulacje UE",
  "Landing.killerFeature.source7.subtitle": "GDPR, NIS2, DORA, AI Act",
  "Landing.killerFeature.source8": "Ogłoszenia zamówień",
  "Landing.killerFeature.source8.subtitle": "BZP/TED",

  // Landing page - Use Cases section
  "Landing.useCases.title": "Jak to działa w praktyce",
  "Landing.useCases.tab1.title": "Due Diligence w minuty, nie tygodnie",
  "Landing.useCases.tab1.description":
    "Wgraj setki umów, lease'ów i dokumentów korporacyjnych. AnyLawyer automatycznie zidentyfikuje kluczowe postanowienia, ryzyka i niestandardowe klauzule – każda z cytowaniem do konkretnego paragrafu.",
  "Landing.useCases.tab2.title": "Badanie stanu prawnego w sekundy",
  "Landing.useCases.tab2.description":
    "Zadaj pytanie dotyczące dowolnej kwestii prawnej. AnyLawyer przeszuka Twoje dokumenty ORAZ polskie akty prawne i orzecznictwo, dając Ci kompleksową odpowiedź z powiązanymi źródłami.",
  "Landing.useCases.tab3.title": "Raporty i notatki prawne jednym kliknięciem",
  "Landing.useCases.tab3.description":
    "Wygeneruj profesjonalne memorandum prawne, raport due diligence lub streszczenie dla klienta bezpośrednio z analizy dokumentów. Eksportuj do DOCX.",
  "Landing.useCases.tab4.title": "Każde słowo ma znaczenie",
  "Landing.useCases.tab4.description":
    "Wgraj transkrypty przesłuchań, zeznań czy stenogramów. AnyLawyer wykryje niespójności, kluczowe przyznania i wzorce argumentacji.",
  "Landing.useCases.tab5.title": "Twój asystent na każde pytanie",
  "Landing.useCases.tab5.description":
    "Czy ta klauzula jest zgodna z RODO? Jaki jest termin przedawnienia? Jakie są wymogi formalne pozwu? Pytaj swobodnie – odpowiedzi oparte na polskim prawie.",

  // Landing page - Trust section
  "Landing.trust.title": "Każda odpowiedź poparta źródłem.",
  "Landing.trust.description":
    "W pracy prawnika nie ma miejsca na zgadywanie. AnyLawyer działa inaczej niż generyczne chatboty – każde twierdzenie jest powiązane z konkretnym fragmentem dokumentu lub aktu prawnego.",
  "Landing.trust.citations.title": "Cytowania ze źródła",
  "Landing.trust.citations.description":
    "Każda odpowiedź zawiera klikalne odwołanie do konkretnego miejsca w dokumencie. Możesz zweryfikować jednym kliknięciem.",
  "Landing.trust.auditTrail.title": "Ścieżka rozumowania",
  "Landing.trust.auditTrail.description":
    "Zobacz krok po kroku, jak AI doszło do wniosku. Pełna transparentność procesu myślenia.",
  "Landing.trust.depthControl.title": "Kontrola głębokości analizy",
  "Landing.trust.depthControl.description":
    "Sam decydujesz, jak dokładną analizę chcesz – od szybkiego przeglądu po dogłębne badanie. Regulowana moc rozumowania AI.",

  // Landing page - Benefits section
  "Landing.benefits.title": "Pracuj mądrzej, nie ciężej",
  "Landing.benefits.description":
    "AnyLawyer zmienia sposób, w jaki prawnicy radzą sobie z pracą opartą na dokumentach.",

  // Landing page - How it works section
  "Landing.howItWorks.title": "Gotowy w kilka minut",
  "Landing.howItWorks.description":
    "Trzy proste kroki, aby zmienić Twój proces badań prawnych.",
  "Landing.howItWorks.step1.title": "Wgraj dokumenty",
  "Landing.howItWorks.step1.description":
    "Przeciągnij i upuść umowy, pisma lub całe akta sprawy. AnyLawyer przetwarza dokumenty tekstowe, arkusze kalkulacyjne, obrazy i skany – ponad 10 obsługiwanych formatów.",
  "Landing.howItWorks.step2.title": "Zadaj pytanie",
  "Landing.howItWorks.step2.description":
    "Zadawaj złożone pytania prawne w naturalnym języku. Nasze AI analizuje cały korpus dokumentów, aby znaleźć precyzyjne odpowiedzi.",
  "Landing.howItWorks.step3.title": "Otrzymaj odpowiedź z cytatami",
  "Landing.howItWorks.step3.description":
    "Otrzymuj szczegółową analizę z precyzyjnymi cytatami. Eksportuj do DOCX i udostępniaj zespołowi.",

  // Landing page - Testimonials section
  "Landing.testimonials.title": "Zaufanie profesjonalistów prawnych",
  "Landing.testimonials.description": "Zobacz, co prawnicy mówią o AnyLawyer.",
  "Landing.testimonials.1.quote":
    "AnyLawyer zmienił sposób, w jaki prowadzimy due diligence. To, co zajmowało tydzień, robimy teraz w dwa dni. Każda odpowiedź ma źródło – wreszcie mogę zaufać narzędziu AI.",
  "Landing.testimonials.1.name": "Katarzyna Nowak",
  "Landing.testimonials.1.title": "Partner, Prawo korporacyjne",
  "Landing.testimonials.2.quote":
    "Jako prawnik in-house odpowiadam na dziesiątki pytań dziennie. Dostęp do polskich aktów prawnych wprost z narzędzia to game changer – nie muszę przeskakiwać między LEX-em a ChatGPT.",
  "Landing.testimonials.2.name": "Michał Kowalski",
  "Landing.testimonials.2.title": "Prawnik in-house",
  "Landing.testimonials.3.quote":
    "Oceniliśmy kilka narzędzi prawnych AI. AnyLawyer był jedynym, który spełnił nasze wymagania bezpieczeństwa dla danych klientów, jednocześnie dostarczając naprawdę użyteczne wyniki.",
  "Landing.testimonials.3.name": "Anna Wiśniewska",
  "Landing.testimonials.3.title": "Partner zarządzający",

  // Landing page - Bento grid features
  "Landing.bento.title": "Zmień swoją firmę prawną",
  "Landing.bento.description":
    "AnyLawyer pomaga prawnikom oszczędzać czas, redukować błędy i uzyskiwać głębsze wnioski z dokumentów.",
  "Landing.bento.dueDiligence.title": "Automatyczne due diligence",
  "Landing.bento.dueDiligence.description":
    "Identyfikuj i wyciągaj kluczowe postanowienia z setek dokumentów natychmiastowo. Automatyzuj przegląd umów, dokumentacji najmu i materiałów dowodowych z dokładnością wspartą cytatami.",
  "Landing.bento.caseResearch.title": "Natychmiastowy research sprawy",
  "Landing.bento.caseResearch.description":
    "Koniec z ręcznym przeszukiwaniem. Zadawaj złożone pytania całemu plikowi sprawy i otrzymuj odpowiedzi zakorzenione w dowodach.",
  "Landing.bento.reportGen.title": "Generowanie dokumentów",
  "Landing.bento.reportGen.description":
    "Generuj dokładne i kompleksowe notatki prawne oraz raporty dla klienta bezpośrednio na podstawie dokumentów sprawy.",
  "Landing.bento.deposition.title": "Analiza zeznań i protokołów",
  "Landing.bento.deposition.description":
    "Przetwarzaj i analizuj transkrypty sądowe, aby wykryć niespójności, kluczowe przyznania i wzorce argumentacji.",
  "Landing.bento.citations.title": "Cytaty osadzone w źródłach",
  "Landing.bento.citations.description":
    "Każde stwierdzenie jest bezpośrednio powiązane z tekstem źródłowym do natychmiastowej weryfikacji. Zero czarnej skrzynki.",
  "Landing.bento.auditTrail.title": "Przejrzyste rozumowanie",
  "Landing.bento.auditTrail.description":
    "Prześledź rozumowanie AI krok po kroku. Zrozum dokładnie, jak osiągnięto wniosek, zanim doradzisz klientowi.",
  "Landing.bento.questions.q1": "Jakie są kluczowe postanowienia w sekcji 3.2?",
  "Landing.bento.questions.q2": "Kiedy wygasa ta umowa?",
  "Landing.bento.questions.q3":
    "Jakie są moje obowiązki wynikające z tej umowy?",
  "Landing.bento.questions.q4":
    "Czy w tym dokumencie jest klauzula o zakazie konkurencji?",
  "Landing.bento.questions.q5": "Jakie są warunki rozwiązania umowy?",
  "Landing.bento.questions.q6": "Kim są zaangażowane strony?",
  "Landing.bento.questions.q7": "Jakie są warunki płatności?",
  "Landing.bento.questions.q8": "Czy są jakieś klauzule odszkodowawcze?",
  "Landing.bento.questions.q9": "Jakie prawo jest właściwe?",
  "Landing.bento.questions.q10": "Jak można zmienić tę umowę?",

  // Landing page - Complete Legal Toolkit section
  "Landing.toolkit.title": "Kompletny zestaw narzędzi prawnych",
  "Landing.toolkit.description":
    "Wszystko, czego potrzebujesz do zarządzania złożonymi procesami prawnymi w jednej bezpiecznej platformie.",
  "Landing.toolkit.dataHeavy.title": "Stworzony dla sporów opartych na danych",
  "Landing.toolkit.dataHeavy.description":
    "Głęboka analiza kontekstu AnyLawyer obsługuje setki tysięcy dokumentów na projekt, umożliwiając pełne odkrywanie całego korpusu.",
  "Landing.toolkit.citations.title": "Cytaty i weryfikacja",
  "Landing.toolkit.citations.description":
    "Wszystkie odpowiedzi są wsparte precyzyjnymi cytatami z oryginalnego dokumentu. Ufaj, ale weryfikuj.",
  "Landing.toolkit.privateTenant.title": "Prywatne wdrożenie",
  "Landing.toolkit.privateTenant.description":
    "Opcjonalne wdrożenie na własnej prywatnej infrastrukturze chmurowej. Wspieramy AWS, GCP i Azure.",
  "Landing.toolkit.multiStep.title": "Wieloetapowe rozumowanie prawne",
  "Landing.toolkit.multiStep.description":
    "Nasze AI rozbija złożone zapytania prawne na logiczne kroki, aby znaleźć odpowiedzi ukryte w setkach plików.",
  "Landing.toolkit.integration.title": "Integracja z ekosystemem",
  "Landing.toolkit.integration.description":
    "Integracja z Google Drive i OneDrive dostępna; SharePoint wkrótce.",
  "Landing.toolkit.dataIsolation.title": "Ścisła izolacja danych",
  "Landing.toolkit.dataIsolation.description":
    "Izolacja na poziomie projektu zapewnia, że dane klientów nigdy się nie krzyżują. Szyfrowane w spoczynku i w tranzycie. Hosting w UE.",
  "Landing.toolkit.export.title": "Eksport do DOCX",
  "Landing.toolkit.export.description":
    "Eksportuj odpowiedzi do formatu DOCX dla łatwego udostępniania i współpracy.",
  "Landing.toolkit.team.title": "Współpraca zespołowa",
  "Landing.toolkit.team.description":
    "Udostępniaj dokumenty, wnioski, prompty i wyniki wyszukiwania swojemu zespołowi. Twórz współpracujące przestrzenie do badania spraw i dzielenia się wiedzą.",
  "Landing.toolkit.comingSoon": "Wkrótce",

  // Landing page - Security section
  "Landing.security.title": "Gotowość enterprise i suwerenność danych",
  "Landing.security.description":
    "Rozumiemy, że poufność klienta jest najważniejsza. Dlatego AnyLawyer jest zbudowany z architekturą bezpieczeństwa na pierwszym miejscu, zaprojektowaną dla nowoczesnej firmy prawnej.",
  "Landing.security.encryption":
    "Szyfrowanie AES-256 w spoczynku (AWS KMS), TLS 1.2+ w tranzycie",
  "Landing.security.isolation": "Ścisła izolacja projektów i danych",
  "Landing.security.zeroTraining":
    "Polityka zero-treningu: trenowanie wyłączone na poziomie API, umowy DPA ze wszystkimi podwykonawcami",
  "Landing.security.privateCloud":
    "Dostępne opcje dedykowanego wdrożenia w prywatnej chmurze",
  "Landing.security.encryptionLabel": "Szyfrowanie",
  "Landing.security.dataResidency": "Lokalizacja danych",
  "Landing.security.dataResidencyValue": "UE (zgodność z RODO)",
  "Landing.security.auditTrailLabel": "Przejrzyste rozumowanie",
  "Landing.security.auditTrailValue": "Niezmienne logi",

  // Landing page - Pricing section
  "Landing.pricing.title": "Prosty, transparentny cennik",
  "Landing.pricing.description":
    "Wybierz plan, który najlepiej odpowiada potrzebom Twojej firmy. Bez ukrytych opłat, bez długoterminowych zobowiązań.",

  // Free plan
  // Plus plan

  // Pro plan
  // Enterprise plan
  "Landing.pricing.contactSales": "Kontakt z nami",

  "Landing.pricing.getStarted": "Rozpocznij",

  // Features
  // Landing page - FAQ section
  "Landing.faq.title": "Często",
  "Landing.faq.title2": "zadawane",
  "Landing.faq.title3": "pytania",
  "Landing.faq.subtitle":
    "Wszystko, co musisz wiedzieć o produkcie i rozliczeniach.",
  "Landing.faq.q1": "Czy AnyLawyer może halucynować jak ChatGPT?",
  "Landing.faq.a1":
    "AnyLawyer został zaprojektowany specjalnie, żeby tego unikać. Każda odpowiedź jest oparta na konkretnym fragmencie dokumentu lub aktu prawnego i zawiera klikalne cytowanie. Jeśli AI nie znajdzie odpowiedzi w źródłach – powie Ci o tym wprost, zamiast zgadywać.",
  "Landing.faq.q2": "Czy moje dane klientów są bezpieczne?",
  "Landing.faq.a2":
    "Tak. Twoje dokumenty są szyfrowane AES-256, przechowywane w EU i NIGDY nie służą do trenowania modeli AI. Każdy projekt jest izolowany – dane jednego klienta nie mają kontaktu z danymi innego.",
  "Landing.faq.q3": "Jakie polskie źródła prawa są dostępne?",
  "Landing.faq.a3":
    "Dziennik Ustaw, Monitor Polski, orzecznictwo sądów powszechnych i SN, orzeczenia Trybunału Konstytucyjnego, interpretacje podatkowe, orzeczenia KIO (zamówienia publiczne), regulacje UE (GDPR, NIS2, DORA, AI Act i inne) oraz ogłoszenia zamówień publicznych (BZP/TED). Baza jest aktualizowana automatycznie.",
  "Landing.faq.q4": "Ile dokumentów mogę przetworzyć?",
  "Landing.faq.a4":
    "Setki tysięcy dokumentów na projekt. AnyLawyer został zbudowany do obsługi masowych analiz typu due diligence, discovery czy audit regulacyjny.",
  "Landing.faq.q5":
    "Czy mogę zintegrować AnyLawyer z moim systemem zarządzania dokumentami?",
  "Landing.faq.a5":
    "Obecnie dostępne są integracje z Google Drive i OneDrive. Dla klientów Enterprise oferujemy dedykowane integracje.",
  "Landing.faq.q6": "Czy mogę przetestować przed zakupem?",
  "Landing.faq.a6":
    "Oczywiście. Każdy plan zaczyna się od 14-dniowego darmowego okresu próbnego. Bez karty kredytowej, bez zobowiązań.",

  // Landing page - Thesis section
  "Landing.thesis.headline":
    "Tysiące stron. Jedna rozmowa. Każda odpowiedź z cytatem.",
  "Landing.thesis.subline":
    "Wgraj akta sprawy, zadaj pytanie w zwykłym języku i otrzymaj odpowiedź ze wskazaniem dokładnego akapitu. Bez halucynacji.",

  // Landing page - Capabilities section
  "Landing.cap.analysis.title": "Głęboka analiza dokumentów",
  "Landing.cap.analysis.body":
    "Wgraj setki umów, pism procesowych lub dokumentów z akt sprawy. Nasze modele czytają każdą stronę i pozwalają przeszukiwać cały korpus – wydobywając klauzule, ryzyka i wzorce, których znalezienie zajęłoby człowiekowi tygodnie.",
  "Landing.cap.citations.title": "Każde twierdzenie ze źródłem",
  "Landing.cap.citations.body":
    "Żadnych odpowiedzi na zaufanie. Każda odpowiedź prowadzi do konkretnego akapitu, strony i dokumentu. Kliknij, by zweryfikować w sekundy.",
  "Landing.cap.security.title": "Poufność w architekturze",
  "Landing.cap.security.body":
    "Szyfrowanie AES-256 w spoczynku i w transmisji. Dane wyłącznie w UE. Ścisła izolacja organizacji. Polityka zero-treningu – Twoje dane nigdy nie są używane do ulepszania modeli. Dostępne wdrożenie w chmurze prywatnej.",
  "Landing.cap.workflow.title": "Od wgrania do notatki w minuty",
  "Landing.cap.workflow.body":
    "Wgraj, zapytaj, eksportuj. Generuj notatki DOCX, dziel się wynikami z kolegami i rozwijaj poprzednie rozmowy. Integracja z Google Drive i OneDrive zapewnia synchronizację dokumentów.",

  // Landing page - Security summary (on landing page)
  "Landing.securitySummary.title": "Twoje dane są Twoje. Kropka.",
  "Landing.securitySummary.zeroTraining.title": "Zero-training policy",
  "Landing.securitySummary.zeroTraining.description":
    "Twoje dokumenty i pytania NIGDY nie służą do trenowania modeli AI. To nie jest obietnica – to architektura systemu.",
  "Landing.securitySummary.encryption.title": "Szyfrowanie AES-256",
  "Landing.securitySummary.encryption.description":
    "Dane szyfrowane w spoczynku i w transmisji. Standard bankowy.",
  "Landing.securitySummary.hosting.title": "Hosting w EU / GDPR",
  "Landing.securitySummary.hosting.description":
    "Dane przetwarzane wyłącznie na serwerach w Unii Europejskiej. Pełna zgodność z RODO.",
  "Landing.securitySummary.isolation.title": "Izolacja danych per projekt",
  "Landing.securitySummary.isolation.description":
    "Dane jednego klienta nigdy nie krzyżują się z danymi innego. Każdy projekt to osobny, hermetyczny kontener.",
  "Landing.securitySummary.privateCloud.title": "Prywatna chmura (Enterprise)",
  "Landing.securitySummary.privateCloud.description":
    "Dla kancelarii z najwyższymi wymogami – wdrożenie na Twojej własnej infrastrukturze AWS, GCP lub Azure.",
  "Landing.securitySummary.cta": "Dowiedz się więcej o bezpieczeństwie",

  // Security page
  "Security.metaTitle": "Bezpieczeństwo — AnyLawyer",
  "Security.metaDescription":
    "Dowiedz się, jak AnyLawyer chroni Twoje dane dzięki zabezpieczeniom klasy enterprise, hostingowi w EU i polityce zero-training.",
  "Security.hero.title": "Twoje dane są Twoje. Kropka.",
  "Security.hero.description":
    "Bezpieczeństwo klasy enterprise to nie dodatek — to fundament AnyLawyer. Każda decyzja architektoniczna zaczyna się od ochrony danych.",
  "Security.faq.title": "FAQ dotyczące bezpieczeństwa",
  "Security.faq.q1": "Czy moje dane klientów są bezpieczne?",
  "Security.faq.a1":
    "Tak. Twoje dokumenty są szyfrowane AES-256, przechowywane w EU i NIGDY nie służą do trenowania modeli AI. Każdy projekt jest izolowany – dane jednego klienta nie mają kontaktu z danymi innego.",
  "Security.faq.q2":
    "Czy mogę zintegrować AnyLawyer z moim systemem zarządzania dokumentami?",
  "Security.faq.a2":
    "Obecnie dostępne są integracje z Google Drive i OneDrive. Dla klientów Enterprise oferujemy dedykowane integracje.",
  "Security.docsLink":
    "Przeczytaj pełną dokumentację bezpieczeństwa i prywatności",
  "Security.cta.title": "Chcesz zobaczyć to w akcji?",
  "Security.cta.button": "Umów demo",

  // About page
  "About.metaTitle": "O nas — AnyLawyer",
  "About.metaDescription":
    "Poznaj zespół AnyLawyer — tworzymy narzędzia AI, które wspierają prawników w codziennej pracy.",
  "About.hero.title": "O AnyLawyer",
  "About.hero.description":
    "Naszą misją jest wspieranie prawników narzędziami AI, które naprawdę rozumieją prawo.",
  "About.mission.title": "Nasza Misja",
  "About.mission.description":
    "Wierzymy, że sztuczna inteligencja powinna wzmacniać prawników — nie zastępować ich. AnyLawyer powstał, by dać każdemu prawnikowi w Polsce narzędzie, któremu może zaufać: szybkie, precyzyjne i osadzone w polskim prawie. Budujemy AI, który cytuje źródła zamiast zgadywać, chroni dane zamiast je eksploatować i oszczędza godziny pracy zamiast generować kolejne wątpliwości. Bo dobra technologia nie komplikuje — upraszcza.",
  "About.leadership.title": "Twórcy",
  "About.leadership.michal.role": "CEO",
  "About.leadership.michal.bio":
    "Profesor AI i prawa. Doradca AI Office ds. dużych modeli językowych. Lider GRAI przy Ministerstwie Cyfryzacji. Założyciel DSK Kancelaria (ponad 100 pracowników).",
  "About.leadership.maciej.role": "CTO",
  "About.leadership.maciej.bio":
    "Engineering Manager w startupach w USA. Były Software Engineer w Riot Games. Były Solutions Architect w Allegro.",
  "About.leadership.rafal.role": "Head of AI",
  "About.leadership.rafal.bio":
    "Były AI Tech Lead w Zapier. Były AI Tech Lead w Vendr.",
  "About.company.title": "Dane spółki",
  "About.company.name": "Nazwa",
  "About.company.nameValue": "ANYLAWYER PROSTA SPÓŁKA AKCYJNA",
  "About.company.registry": "Rejestr",
  "About.company.registryValue": "Rejestr Przedsiębiorców",
  "About.company.krs": "Numer KRS",
  "About.company.krsValue": "0001223474",
  "About.company.nip": "NIP",
  "About.company.nipValue": "7792601628",
  "About.company.regon": "REGON",
  "About.company.regonValue": "54397036000000",
  "About.company.legalForm": "Forma prawna",
  "About.company.legalFormValue": "Prosta Spółka Akcyjna",

  // Landing page - Product showcase section
  "Landing.showcase.label": "Platforma",
  "Landing.showcase.headline": "Stworzona pod realne potrzeby prawników",
  "Landing.showcase.subline":
    "Niezależnie czy to 200-stronicowa umowa czy 10 000 dokumentów z akt sprawy, AnyLawyer skaluje się do złożoności prawdziwej pracy prawnej.",

  // Landing page - Stats
  "Landing.stat.speed": "Szybsza analiza dokumentów",
  "Landing.stat.encryption": "Standard szyfrowania",
  "Landing.stat.residency": "Lokalizacja danych",
  "Landing.stat.training": "Dane użyte do treningu",

  // Landing page - Process section
  "Landing.process.headline": "Trzy proste kroki.",

  // Landing page - Final CTA section
  "Landing.cta.title": "Gotowy, by pracować efektywniej?",
  "Landing.cta.description":
    "Dołącz do prawników, którzy już oszczędzają godziny tygodniowo. 14 dni za darmo, bez karty kredytowej.",
  "Landing.cta.primaryCta": "Zacznij za darmo",
  "Landing.cta.secondaryCta": "Umów demo",

  // Landing page - Footer
  "Landing.footer.tagline":
    "Wspieramy profesjonalistów prawnych najnowszymi narzędziami AI.",
  "Landing.footer.product": "Produkt",
  "Landing.footer.resources": "Zasoby",
  "Landing.footer.docs": "Dokumentacja",
  "Landing.footer.changelog": "Changelog",
  "Landing.footer.taxInterpretations": "Interpretacje podatkowe",
  "Landing.footer.legal": "Dokumenty prawne",
  "Landing.footer.privacyPolicy": "Polityka prywatności",
  "Landing.footer.termsOfService": "Regulamin",
  "Landing.footer.contactUs": "Napisz do nas",
  "Landing.footer.faq": "FAQ",
  "Landing.footer.company": "Firma",
  "Landing.footer.aboutUs": "O nas",
  "Landing.footer.copyright": "© 2025 AnyLawyer. Wszelkie prawa zastrzeżone.",

  // Organization Documents page
  "Manage documents that are available across all projects in your organization":
    "Zarządzaj dokumentami dostępnymi we wszystkich projektach organizacji",
  "Add Documents": "Dodaj dokumenty",
  "Add Organization Documents": "Dodaj dokumenty organizacji",
  "Upload Files": "Prześlij pliki",
  "Choose Files": "Wybierz pliki",
  "You can select multiple files. Max file size: {{size}}MB each.":
    "Możesz wybrać wiele plików. Maksymalny rozmiar pliku: {{size}}MB.",
  "Selected Files ({{count}})": "Wybrane pliki ({{count}})",
  "Document name...": "Nazwa dokumentu...",
  "Upload {{count}} Document": "Prześlij {{count}} dokument",
  "Upload {{count}} Documents": "Prześlij {{count}} dokumenty",
  "Upload Progress": "Postęp przesyłania",
  "{{completed}} / {{total}} completed": "{{completed}} / {{total}} ukończono",
  "No organization documents yet. Add your first one above.":
    "Brak dokumentów organizacji. Dodaj pierwszy dokument powyżej.",
  Name: "Nazwa",
  Created: "Utworzono",
  Actions: "Akcje",
  "uploading {{progress}}%": "przesyłanie {{progress}}%",
  queued: "w kolejce",
  "upload complete": "przesłano",
  "upload failed": "błąd przesyłania",
  "Unsupported file type": "Nieobsługiwany typ pliku",
  "Duplicate file": "Duplikat pliku",
  "{{file}} is already selected.": "{{file}} jest już wybrany.",
  "No files selected": "Nie wybrano plików",
  "Please select at least one file to upload.":
    "Wybierz przynajmniej jeden plik do przesłania.",
  "Missing names": "Brak nazw",
  "Please provide names for all selected documents.":
    "Podaj nazwy dla wszystkich wybranych dokumentów.",
  "Upload started": "Rozpoczęto przesyłanie",
  "Starting upload of {{count}} document(s).":
    "Rozpoczynam przesyłanie {{count}} dokumentów.",
  "Document uploaded": "Dokument przesłany",
  "{{name}} has been uploaded and is being processed.":
    "{{name}} został przesłany i jest przetwarzany.",
  "Upload timeout": "Przekroczono limit czasu",
  "Document upload is taking longer than expected. Please refresh the page to check if it appeared.":
    "Przesyłanie dokumentu trwa dłużej niż oczekiwano. Odśwież stronę, aby sprawdzić, czy się pojawił.",
  "Document processed": "Dokument przetworzony",
  "{{name}} has been successfully processed.":
    "{{name}} został pomyślnie przetworzony.",
  "Document processing failed": "Przetwarzanie dokumentu nie powiodło się",
  "{{name}} failed to process.": "Nie udało się przetworzyć {{name}}.",
  Ready: "Gotowe",
  Processing: "Przetwarzanie",
  Failed: "Błąd",
  "Unknown error": "Nieznany błąd",
  "No organization documents": "Brak dokumentów organizacji",
  "Preparing shared document": "Przygotowywanie dokumentu współdzielonego",
  "Status updates appear automatically":
    "Aktualizacje statusu pojawią się automatycznie",
  "Are you sure you want to delete this document?":
    "Czy na pewno chcesz usunąć ten dokument?",

  // ELI Legal Acts Browser
  "Legal Acts Database": "Baza aktów prawnych",
  "Browse and search Polish legal acts from the official ELI database":
    "Przeglądaj i wyszukuj polskie akty prawne z oficjalnej bazy ELI",
  "Search acts by title...": "Szukaj aktów po tytule...",
  Search: "Szukaj",
  Clear: "Wyczyść",
  Publishers: "Wydawnictwa",
  "No publishers found. Run the Sejm sync to populate the database.":
    "Nie znaleziono wydawnictw. Uruchom synchronizację Sejmu, aby wypełnić bazę danych.",
  acts: "aktów",
  "total acts": "wszystkich aktów",
  "Legal Acts": "Akty prawne",
  Year: "Rok",
  Position: "Pozycja",
  "All years": "Wszystkie lata",
  Showing: "Wyświetlono",
  Acts: "Akty",
  "Acts from": "Akty z",
  "All acts from this publisher": "Wszystkie akty tego wydawnictwa",
  "No acts found for the selected criteria.":
    "Nie znaleziono aktów spełniających wybrane kryteria.",
  Previous: "Poprzednia",
  of: "z",
  Next: "Następna",
  "Search results for": "Wyniki wyszukiwania dla",
  "No acts found matching your search.":
    "Nie znaleziono aktów pasujących do wyszukiwania.",
  Address: "Adres",
  Date: "Data",
  "Recently ingested acts": "Ostatnio dodane akty",
  "Last 10 acts added to the database, ordered by creation time":
    "Ostatnie 10 aktów dodanych do bazy danych, posortowane wg czasu utworzenia",
  "Created at": "Utworzono",
  "Synced at": "Zsynchronizowano",
  Indexed: "Zindeksowano",
  "Turbopuffer indexing": "Indeksowanie Turbopuffer",
  "Coverage of acts indexed into Turbopuffer":
    "Pokrycie aktów zindeksowanych w Turbopuffer",
  "Total acts in DB": "Łącznie aktów w DB",
  "Indexed acts": "Zindeksowane akty",
  Coverage: "Pokrycie",
  Publisher: "Wydawnictwo",
  "Total in DB": "Łącznie w DB",
  Yes: "Tak",
  No: "Nie",
  "In force": "Obowiązujący",
  "Not in force": "Nieobowiązujący",
  Metadata: "Metadane",
  ELI: "ELI",
  Type: "Typ",
  "Announcement Date": "Data ogłoszenia",
  "Entry Into Force": "Wejście w życie",
  "Valid From": "Ważny od",
  Promulgation: "Promulgacja",
  "Expiration Date": "Data wygaśnięcia",
  "Repeal Date": "Data uchylenia",
  "Legal Status Date": "Data statusu prawnego",
  "Change Date": "Data zmiany",
  Volume: "Tom",
  "Authorized Body": "Organ uprawniony",
  Obligated: "Zobowiązani",
  "Released By": "Wydany przez",
  Comments: "Komentarze",
  References: "Odniesienia",
  references: "odniesień",
  "No references found for this act.": "Brak odniesień dla tego aktu.",
  "No ELI ID": "Brak identyfikatora ELI",
  Texts: "Teksty",
  "Text Versions": "Wersje tekstowe",
  "Full Act PDF": "Pełny akt PDF",
  "Consolidated document": "Dokument skonsolidowany",
  "No text files available for this act.":
    "Brak dostępnych plików tekstowych dla tego aktu.",
  "Consolidated text": "Tekst jednolity",
  "Published text": "Tekst opublikowany",
  "Uniform text": "Tekst ujednolicony",
  "Historical text": "Tekst historyczny",
  "Initial text": "Tekst początkowy",
  Text: "Tekst",
  Directives: "Dyrektywy",
  "EU Directives": "Dyrektywy UE",
  "No EU directives linked to this act.":
    "Brak dyrektyw UE powiązanych z tym aktem.",
  Prints: "Druki",
  "Parliamentary Prints": "Druki sejmowe",
  "No parliamentary prints linked to this act.":
    "Brak druków sejmowych powiązanych z tym aktem.",
  Print: "Druk",
  Term: "Kadencja",
  View: "Zobacz",
  "Full Text": "Pełny tekst",
  "Untitled Act": "Akt bez tytułu",
  Title: "Tytuł",
  "Download PDF": "Pobierz PDF",
  "Official PDF": "Oficjalny PDF",
  "Open in new tab": "Otwórz w nowej karcie",
  "PDF Preview": "Podgląd PDF",
  "Sejm API": "API Sejmu",
  "No full text available for this act.":
    "Pełny tekst nie jest dostępny dla tego aktu.",
  "Full text not available. See PDF preview.":
    "Pełny tekst niedostępny. Zobacz podgląd PDF.",
  Structure: "Struktura",
  "Search provisions...": "Szukaj przepisów...",
  "Semantic search acts...": "Wyszukiwanie semantyczne aktów...",
  "Search by title": "Szukaj po tytule",
  "Search provisions": "Szukaj przepisów",
  "Articles indexed": "Zaindeksowane artykuły",
  "provisions in": "przepisów w",
  provisions: "przepisów",
  "results for": "wyniki dla",
  "Tool output format": "Format wyjścia narzędzia",
  "Raw JSON output": "Surowy JSON",
  Any: "Dowolny",
  "Exact phrase filter": "Filtr dokładnej frazy",
  Book: "Księga",
  Part: "Część",
  Branch: "Dział",
  Chapter: "Rozdział",
  "Sub-chapter": "Oddział",
  "Art.": "Art.",
  "§": "§",
  "ust.": "ust.",
  pkt: "pkt",
  "lit.": "lit.",
  "Search files and folders...": "Szukaj plików i folderów...",
  "No results found": "Nie znaleziono wyników",
  "Attach to project": "Dołącz do projektu",
  "Detach from project": "Odłącz od projektu",

  // Onboarding
  "onboarding.welcome.title": "Witaj w AnyLawyer!",
  "onboarding.welcome.content":
    "Pozwól, że Ci opowiemy o aplikacji. Ten krótki przewodnik pomoże Ci rozpocząć analizę dokumentów prawnych.",
  "onboarding.askQuestion.title": "Zadawaj Pytania",
  "onboarding.askQuestion.content":
    "Wpisz tutaj swoje pytanie prawne. Nasza AI przeanalizuje Twoje dokumenty i udzieli odpowiedzi z cytatami.",
  "onboarding.improvePrompt.title": "Ulepsz Swoje Pytanie",
  "onboarding.improvePrompt.content":
    "Kliknij tutaj, aby automatycznie przepisać pytanie na bardziej precyzyjne i skuteczne.",
  "onboarding.projectSelector.title": "Wybierz Projekt",
  "onboarding.projectSelector.content":
    "Wybierz istniejący projekt lub utwórz nowy. Projekty pomagają organizować dokumenty i rozmowy.",
  "onboarding.createProject.title": "Utwórz Nowy Projekt",
  "onboarding.createProject.content":
    "Tutaj możesz utworzyć nowy projekt. Nadaj mu nazwę i zacznij dodawać dokumenty prawne.",
  "onboarding.projects.title": "Twoje Projekty",
  "onboarding.projects.content":
    "Tutaj znajdziesz wszystkie swoje projekty. Kliknij na dowolny projekt, aby go otworzyć i kontynuować pracę. Jesteś gotowy!",

  // Project Onboarding
  "onboarding.project.welcome.title": "Widok Projektu",
  "onboarding.project.welcome.content":
    "To jest Twoja przestrzeń projektowa. Tutaj możesz organizować dokumenty, zarządzać rozmowami i uzyskiwać odpowiedzi wspomagane AI.",
  "onboarding.project.upload.title": "Dodaj Dokumenty",
  "onboarding.project.upload.content":
    "Przeciągnij i upuść pliki tutaj lub kliknij, aby dodać dokumenty prawne. Obsługiwane formaty to PDF, Word i inne.",
  "onboarding.project.legalDocs.title": "Biblioteka Dokumentów Prawnych",
  "onboarding.project.legalDocs.content":
    "Uzyskaj dostęp do naszej biblioteki dokumentów prawnych. Wybierz te, które są istotne dla Twojego projektu.",
  "onboarding.project.context.title": "Kontekst Projektu",
  "onboarding.project.context.content":
    "Dodaj opis, aby dostarczyć kontekst dla AI. Pomoże to w generowaniu dokładniejszych i trafniejszych odpowiedzi.",
  "onboarding.project.topics.title": "Wątki Rozmów",
  "onboarding.project.topics.content":
    "Twoje rozmowy są zorganizowane w wątki. Każdy wątek przechowuje oddzielny ciąg pytań i odpowiedzi.",
  "onboarding.project.chat.title": "Zadawaj Pytania",
  "onboarding.project.chat.content":
    "Wpisz tutaj swoje pytanie, aby rozpocząć nową rozmowę. AI przeanalizuje dokumenty projektu i udzieli odpowiedzi.",
  "onboarding.project.settings.title": "Ustawienia Odpowiedzi",
  "onboarding.project.settings.content":
    "Dostosuj głębokość analizy i długość odpowiedzi. Wyższy poziom analizy zapewnia dokładniejsze wyniki.",
  "onboarding.project.googleDrive.title": "Integracja z Google Drive",
  "onboarding.project.googleDrive.content":
    "Połącz swój Google Drive, aby uzyskać dostęp do dokumentów bezpośrednio z chmury.",
  "onboarding.project.eureka.title": "Interpretacje podatkowe (Eureka)",
  "onboarding.project.eureka.content":
    "Nowość: włącz „Interpretacje podatkowe (Eureka)”, aby AnyLawyer korzystał ze wszystkich polskich interpretacji podatkowych.",

  // Documentation
  Documentation: "Dokumentacja",
  "Welcome to the AnyLawyer documentation": "Witamy w dokumentacji AnyLawyer",
  "AnyLawyer documentation helps you set up and master our AI-powered legal document intelligence platform. AnyLawyer extracts key data, answers document questions with grounded citations, generates summaries and reports, analyzes court transcripts, and surfaces insights with deep search.":
    "Dokumentacja AnyLawyer pomoże Ci skonfigurować i opanować naszą platformę inteligentnej analizy dokumentów prawnych opartą na AI. AnyLawyer wyodrębnia kluczowe dane, odpowiada na pytania dotyczące dokumentów z uzasadnionymi cytatami, generuje podsumowania i raporty, analizuje transkrypcje sądowe i odkrywa wnioski dzięki głębokiemu wyszukiwaniu.",

  // Audit Log
  "Audit Log": "Dziennik zdarzeń",
  "Activity log for your organization":
    "Dziennik aktywności Twojej organizacji",
  "Track all user actions and changes across your organization":
    "Śledź wszystkie działania użytkowników i zmiany w Twojej organizacji",
  "All actions": "Wszystkie akcje",
  "All users": "Wszyscy użytkownicy",
  "All organizations": "Wszystkie organizacje",
  "Clear filters": "Wyczyść filtry",
  "audit.document_upload": "Przesłano dokument",
  "audit.document_rename": "Zmieniono nazwę dokumentu",
  "audit.document_delete": "Usunięto dokument",
  "audit.project_create": "Utworzono projekt",
  "audit.project_archive": "Zarchiwizowano projekt",
  "audit.project_unarchive": "Przywrócono projekt",
  "audit.project_rename": "Zmieniono nazwę projektu",
  "audit.project_delete": "Usunięto projekt",
  "audit.project_change_visibility": "Zmieniono widoczność projektu",
  "audit.project_update_description": "Zaktualizowano opis projektu",
  "audit.topic_create": "Utworzono wątek",
  "audit.topic_rename": "Zmieniono nazwę wątku",
  "audit.topic_delete": "Usunięto wątek",
  "audit.message_send": "Wysłano wiadomość",
  "No audit logs found": "Nie znaleziono zdarzeń",
  Action: "Akcja",
  Resource: "Zasób",
  Timestamp: "Data",
  Details: "Szczegóły",
  "Rows per page": "Wierszy na stronę",
  entries: "wpisów",
  in: "w",
};
